/**
 * TopStepAccountPage — Reusable Per-Account Dashboard
 *
 * Parameterized 6-tab dashboard for any TopStep account:
 *   Tab 0 — Overview    : KPIs, risk engine, challenge progress, kill switch
 *   Tab 1 — Autopilot   : Instrument selector, start/stop, session decisions, trade log, equity curve, fib ladder
 *   Tab 2 — Kaze Signals: Global Kaze intelligence (shared, not per-account)
 *   Tab 3 — Decision Lab: Read-only signal, chart intelligence, and APGE visualization
 *   Tab 4 — Chart       : TradingView live chart
 *   Tab 5 — Orders      : Open positions, order history
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Grid,
  Typography,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Divider,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Refresh,
  PlayArrow,
  Stop,
  CheckCircle,
  ShowChart,
  MonitorHeart,
  CurrencyExchange,
  Cancel,
  Send,
  Lock,
  LockOpen,
  Air,
  Insights,
} from '@mui/icons-material';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import topstepService, {
  AnalysisSide,
  ChartGrade,
  TopStepContract,
  TopStepRiskState,
  ChallengeProgress,
  AutopilotStatus,
  KazeFuturesSignal,
  KazeSignalsMeta,
  TopStepAnalysisVisualization,
  TopStepAccountStatus,
  TopStepInstrumentMeta,
  TopStepInstrumentRegistryResponse,
  TopStepOrder,
  TopStepPosition,
} from '../services/topstepService';
import { createTopStepDatafeed, normalizeTopStepSymbolKey, registerContract } from '../utils/topstepDatafeed';
import FibonacciLadderViz, { FibLadderPosition } from '../components/topstep/FibonacciLadderViz';

// ─── Types ──────────────────────────────────────────────

export interface ChallengeParams {
  target: number;
  hardMax: number;
  internalStop: number;
  dailyCap: number;
  accountSize: number;
}

export interface AccountPageProps {
  accountLabel: string;
  chipLabel: string;
  chipColor: string;
  accountMatcher: (accounts: TopStepAccountStatus[]) => TopStepAccountStatus | undefined;
  challengeParams: ChallengeParams;
  accountApi?: TopStepAccountApi;
  datafeedApiBasePath?: string;
}

export interface TopStepAccountApi {
  getAccounts: () => Promise<TopStepAccountStatus[]>;
  getAccountRisk: (accountId: number) => Promise<TopStepRiskState | null>;
  getAccountChallengeProgress: (accountId: number) => Promise<ChallengeProgress | null>;
  getAccountAutopilotStatus: (accountId: number) => Promise<AutopilotStatus | null>;
  getAccountOrders: (accountId: number, status?: number) => Promise<TopStepOrder[]>;
  getAccountPositions: (accountId: number) => Promise<{ positions: TopStepPosition[]; totalUnrealized: number | null; pnlIsRealtime?: boolean; realtimeCount?: number }>;
  getContracts: () => Promise<{ contracts: TopStepContract[]; registry: { symbol: string; id: string; name: string; tickSize: number; tickValue: number }[] }>;
  getInstruments?: () => Promise<TopStepInstrumentRegistryResponse>;
  getKazeFuturesSignals: () => Promise<KazeFuturesSignal[]>;
  getKazeSignalsMeta: () => Promise<KazeSignalsMeta | null>;
  getAccountAnalysisVisualization?: (accountId: number, symbol: string, side?: AnalysisSide) => Promise<TopStepAnalysisVisualization | null>;
  startAccountAutopilot: (accountId: number, instruments: string[]) => Promise<AutopilotStatus['session'] | null>;
  stopAccountAutopilot: (accountId: number) => Promise<AutopilotStatus['session'] | null>;
  clearAccountKillSwitch: (accountId: number) => Promise<void>;
  placeAccountOrder: (accountId: number, params: { contractSymbol: string; side: 'BUY' | 'SELL'; contracts: number; type?: 'market' | 'limit'; limitPrice?: number }) => Promise<TopStepOrder>;
  cancelOrder: (orderId: number) => Promise<boolean>;
  cancelAccountOrder?: (accountId: number, orderId: number) => Promise<boolean>;
}

// ─── Constants ──────────────────────────────────────────

const COLORS = {
  gold:     '#D4AF37',
  platinum: '#E5E4E2',
  blue:     '#4FC3F7',
  green:    '#66BB6A',
  red:      '#EF5350',
  orange:   '#FFA726',
  surface:  '#161B27',
  surface2: '#1E2535',
  surfaceElevated: '#20283A',
  surfaceDark: '#10141F',
  track: 'rgba(255,255,255,0.10)',
  border:   'rgba(255,255,255,0.07)',
  muted:    'rgba(255,255,255,0.45)',
};

const DEFAULT_AP_INSTRUMENTS = ['MNQ', 'MES', 'M2K', 'MGC', 'MCL', 'M6E'];
const INSTRUMENT_GROUP_ORDER = ['Micro Equity', 'Full Equity', 'Metals / Energy', 'FX', 'Rates', 'Other'];

const FALLBACK_INSTRUMENT_OPTIONS: TopStepInstrumentMeta[] = [
  { symbol: 'MNQ', fullName: 'Micro Nasdaq-100', tickSize: 0.25, tickValue: 0.50, stopTicks: 0, targetTicks: 0, riskPerContract: 0, marginPerContract: 0, isDefault: true, isFullSize: false },
  { symbol: 'MES', fullName: 'Micro S&P 500', tickSize: 0.25, tickValue: 1.25, stopTicks: 0, targetTicks: 0, riskPerContract: 0, marginPerContract: 0, isDefault: true, isFullSize: false },
  { symbol: 'M2K', fullName: 'Micro Russell 2000', tickSize: 0.10, tickValue: 0.50, stopTicks: 0, targetTicks: 0, riskPerContract: 0, marginPerContract: 0, isDefault: true, isFullSize: false },
  { symbol: 'MGC', fullName: 'Micro Gold', tickSize: 0.10, tickValue: 1.00, stopTicks: 0, targetTicks: 0, riskPerContract: 0, marginPerContract: 0, isDefault: true, isFullSize: false },
  { symbol: 'MCL', fullName: 'Micro Crude Oil', tickSize: 0.01, tickValue: 1.00, stopTicks: 0, targetTicks: 0, riskPerContract: 0, marginPerContract: 0, isDefault: true, isFullSize: false },
  { symbol: 'M6E', fullName: 'Micro Euro FX', tickSize: 0.00005, tickValue: 6.25, stopTicks: 0, targetTicks: 0, riskPerContract: 0, marginPerContract: 0, isDefault: true, isFullSize: false },
];

function instrumentGroup(option: TopStepInstrumentMeta): string {
  if (['MNQ', 'MES', 'MYM', 'M2K'].includes(option.symbol)) return 'Micro Equity';
  if (['NQ', 'ES', 'RTY', 'NKD'].includes(option.symbol)) return 'Full Equity';
  if (['MGC', 'GC', 'MCL', 'CL', 'SI', 'NG', 'HG'].includes(option.symbol)) return 'Metals / Energy';
  if (option.symbol === 'M6E' || option.symbol.startsWith('6')) return 'FX';
  if (['ZT', 'ZN', 'ZB'].includes(option.symbol)) return 'Rates';
  return 'Other';
}

// ─── Helpers ────────────────────────────────────────────

interface TabPanelProps { children: React.ReactNode; value: number; index: number; }
function TabPanel({ children, value, index }: TabPanelProps) {
  return value === index ? <Box sx={{ pt: 2.5 }}>{children}</Box> : null;
}

function fmt$(n: number, decimals = 0) {
  return `${n < 0 ? '-' : ''}$${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
}

function pnlColor(n: number) { return n >= 0 ? COLORS.green : COLORS.red; }

function statusColor(status: number) {
  if (status === 2) return COLORS.green;
  if (status === 3 || status === 4 || status === 5) return COLORS.red;
  if (status === 1) return COLORS.blue;
  return COLORS.muted;
}

function statusLabel(status: number) {
  const labels: Record<number, string> = { 0: 'Pending', 1: 'Working', 2: 'Filled', 3: 'Cancelled', 4: 'Rejected', 5: 'Expired' };
  return labels[status] ?? `Status ${status}`;
}

function orderSideLabel(side: number) { return side === 0 ? 'BUY' : 'SELL'; }
function orderTypeLabel(type: number) {
  const labels: Record<number, string> = { 1: 'Limit', 2: 'Market', 3: 'Stop', 4: 'StopLmt' };
  return labels[type] ?? `Type${type}`;
}
function fmtPrice(price: number | undefined): string {
  if (price == null) return '—';
  return price < 100 ? price.toFixed(4) : price.toFixed(2);
}

function directionColor(d: string) {
  if (d === 'LONG' || d === 'BULLISH') return COLORS.green;
  if (d === 'SHORT' || d === 'BEARISH') return COLORS.red;
  return COLORS.muted;
}

function scoreBar(score: number) {
  const pct = Math.round(score * 100);
  const color = pct >= 70 ? COLORS.green : pct <= 30 ? COLORS.red : COLORS.orange;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <LinearProgress variant="determinate" value={pct}
        sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.08)',
          '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 3 } }} />
      <Typography variant="caption" sx={{ color, fontWeight: 700, minWidth: 36, textAlign: 'right' }}>
        {pct}%
      </Typography>
    </Box>
  );
}

const GRADE_POINTS: Record<ChartGrade, number> = { F: 0, C: 1, B: 2, A: 3, A_PLUS: 4 };

function gradePoints(grade?: ChartGrade | string): number {
  if (!grade) return 0;
  return GRADE_POINTS[grade as ChartGrade] ?? 0;
}

function gradeColor(grade?: ChartGrade | string): string {
  if (grade === 'A_PLUS' || grade === 'A') return COLORS.green;
  if (grade === 'B') return COLORS.blue;
  if (grade === 'C') return COLORS.orange;
  if (grade === 'F') return COLORS.red;
  return COLORS.muted;
}

function gradeLabel(grade?: ChartGrade | string): string {
  if (!grade) return '—';
  return grade === 'A_PLUS' ? 'A+' : String(grade);
}

function formatAction(action?: string): string {
  if (!action) return '—';
  return action.replace(/_/g, ' ');
}

function scoreColor(value?: number): string {
  if (value == null) return COLORS.muted;
  return value >= 70 ? COLORS.green : value <= 35 ? COLORS.red : COLORS.orange;
}

function pctLabel(value?: number): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${Math.round(value)}%`;
}

function normalizeFuturesSymbol(value?: string): string {
  const raw = normalizeTopStepSymbolKey(value);
  const defaultRoot = DEFAULT_AP_INSTRUMENTS.find(symbol => raw === symbol || raw.startsWith(symbol));
  if (defaultRoot) return defaultRoot;
  return raw.replace(/[FGHJKMNQUVXZ]\d+$/i, '');
}

function registerTopStepChartContract(contract: Pick<TopStepContract, 'id' | 'name' | 'description' | 'tickSize' | 'tickValue' | 'symbolId'>, symbolHint?: string) {
  const info = {
    contractId: contract.id,
    name: contract.name,
    description: contract.description,
    tickSize: contract.tickSize,
    tickValue: contract.tickValue,
    symbolId: contract.symbolId,
  };
  [symbolHint, contract.symbolId, contract.name, normalizeTopStepSymbolKey(contract.name), normalizeTopStepSymbolKey(contract.symbolId)]
    .filter(Boolean)
    .forEach(alias => registerContract(String(alias), info));
}

function signalMatchesSymbol(signal: KazeFuturesSignal, symbol: string): boolean {
  const target = normalizeFuturesSymbol(symbol);
  return [signal.symbol, signal.rootKey, signal.contractName, signal.contractId, signal.description]
    .some(value => normalizeFuturesSymbol(value) === target);
}

function sideFromSignal(signal?: KazeFuturesSignal): AnalysisSide {
  if (signal?.direction === 'LONG') return 'long';
  if (signal?.direction === 'SHORT') return 'short';
  return 'neutral';
}

function compactTime(value: string | number | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function compactDateTime(value: string | number | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function fmtLevel(value: unknown): string {
  if (typeof value === 'number' && Number.isFinite(value)) return value.toFixed(value >= 100 ? 2 : 4);
  if (Array.isArray(value) && value.every(item => typeof item === 'number')) return value.map(item => fmtLevel(item)).join(' - ');
  if (typeof value === 'string') return value;
  return '—';
}

let tradingViewLibraryPromise: Promise<void> | null = null;

function loadTradingViewLibrary(): Promise<void> {
  if ((window as any).TradingView?.widget) return Promise.resolve();
  if (tradingViewLibraryPromise) return tradingViewLibraryPromise;

  tradingViewLibraryPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-redex-tv-library="true"], script[src="/charting_library/charting_library.js"]');
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('TradingView charting library failed to load')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = '/charting_library/charting_library.js';
    script.async = true;
    script.dataset.redexTvLibrary = 'true';
    script.onload = () => resolve();
    script.onerror = () => {
      tradingViewLibraryPromise = null;
      reject(new Error('TradingView charting library failed to load'));
    };
    document.head.appendChild(script);
  });

  return tradingViewLibraryPromise;
}

function finiteLevel(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function numericZone(value: unknown): [number, number] | null {
  if (!Array.isArray(value) || value.length < 2) return null;
  const first = finiteLevel(value[0]);
  const second = finiteLevel(value[1]);
  if (first == null || second == null) return null;
  return first <= second ? [first, second] : [second, first];
}

function epochSeconds(value: string | number | undefined): number | null {
  if (!value) return null;
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? Math.floor(timestamp / 1000) : null;
}

function createLockedLevel(chart: any, time: number, price: unknown, label: string, color: string, style = 2) {
  const level = finiteLevel(price);
  if (level == null) return;
  chart.createShape({ time, price: level }, {
    shape: 'horizontal_line',
    text: `${label} ${fmtLevel(level)}`,
    lock: true,
    disableSelection: true,
    disableSave: true,
    disableUndo: true,
    overrides: {
      linecolor: color,
      textcolor: color,
      linewidth: 1,
      linestyle: style,
      showLabel: true,
    },
  }).catch(() => undefined);
}

function createLockedZone(chart: any, fromTime: number, toTime: number, zone: unknown, color: string) {
  const levels = numericZone(zone);
  if (!levels) return;
  const [low, high] = levels;
  chart.createMultipointShape([
    { time: fromTime, price: low },
    { time: toTime, price: high },
  ], {
    shape: 'rectangle',
    text: `Entry ${fmtLevel(levels)}`,
    lock: true,
    disableSelection: true,
    disableSave: true,
    disableUndo: true,
    filled: true,
    zOrder: 'bottom',
    overrides: {
      color,
      backgroundColor: color,
      fillBackground: true,
      linewidth: 1,
      transparency: 86,
    },
  }).catch(() => undefined);
  createLockedLevel(chart, toTime, low, 'Zone low', color, 2);
  createLockedLevel(chart, toTime, high, 'Zone high', color, 2);
}

function TradingViewPriceContextChart({
  symbol,
  analysisViz,
  datafeedApiBasePath,
  entryZone,
  target,
  invalidation,
  setupColor,
  loading,
}: {
  symbol: string;
  analysisViz: TopStepAnalysisVisualization | null;
  datafeedApiBasePath: string;
  entryZone: unknown;
  target: unknown;
  invalidation: unknown;
  setupColor: string;
  loading: boolean;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetRef = useRef<any>(null);
  const [tvError, setTvError] = useState<string | null>(null);
  const bars = analysisViz?.bars || [];
  const ready = analysisViz?.dataStatus === 'ready' && bars.length > 0;

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !ready) return;

    let cancelled = false;
    setTvError(null);
    container.innerHTML = '';

    if (widgetRef.current) {
      try { widgetRef.current.remove(); } catch { /* noop */ }
      widgetRef.current = null;
    }

    loadTradingViewLibrary()
      .then(() => {
        if (cancelled || !container) return;

        const widget = new (window as any).TradingView.widget({
          container,
          datafeed: createTopStepDatafeed(datafeedApiBasePath),
          library_path: '/charting_library/',
          symbol: `TOPSTEP:${symbol}`,
          autosize: true,
          interval: '60',
          timezone: 'America/New_York',
          theme: 'Dark',
          locale: 'en',
          toolbar_bg: COLORS.surface,
          enable_publishing: false,
          allow_symbol_change: false,
          loading_screen: { backgroundColor: COLORS.surfaceDark, foregroundColor: COLORS.blue },
          disabled_features: [
            'use_localstorage_for_settings',
            'save_chart_properties_to_local_storage',
            'header_saveload',
            'header_symbol_search',
            'symbol_search_hot_key',
            'header_compare',
            'header_screenshot',
            'header_fullscreen_button',
            'left_toolbar',
            'timeframes_toolbar',
          ],
          enabled_features: ['hide_left_toolbar_by_default'],
          overrides: {
            'mainSeriesProperties.style': 1,
            'mainSeriesProperties.showCountdown': true,
          },
        });

        widgetRef.current = widget;
        widget.onChartReady(() => {
          if (cancelled) return;
          const chart = widget.activeChart();
          const firstTime = epochSeconds(bars[0]?.timestamp);
          const lastTime = epochSeconds(bars[bars.length - 1]?.timestamp);
          if (!chart || firstTime == null || lastTime == null) return;

          const addEma = (length: number, color: string, width = 1) => {
            chart.createStudy('Moving Average Exponential', true, false, { length }, {
              'plot.color': color,
              'plot.linewidth': width,
            }).catch(() => undefined);
          };

          addEma(8, COLORS.green, 2);
          addEma(21, COLORS.gold, 2);
          addEma(34, COLORS.blue, 1);
          addEma(50, COLORS.red, 2);
          createLockedZone(chart, firstTime, lastTime, entryZone, setupColor);
          createLockedLevel(chart, lastTime, analysisViz?.indicators?.cloudTop, 'Cloud top', COLORS.blue, 2);
          createLockedLevel(chart, lastTime, analysisViz?.indicators?.cloudBottom, 'Cloud bottom', COLORS.blue, 2);
          createLockedLevel(chart, lastTime, target, 'Target', COLORS.green, 1);
          createLockedLevel(chart, lastTime, invalidation, 'Invalid', COLORS.red, 1);
          createLockedLevel(chart, lastTime, analysisViz?.indicators?.lastClose, 'Last', COLORS.platinum, 3);
        });
      })
      .catch((error: Error) => {
        if (!cancelled) setTvError(error.message || 'TradingView chart unavailable');
      });

    return () => {
      cancelled = true;
      if (widgetRef.current) {
        try { widgetRef.current.remove(); } catch { /* noop */ }
        widgetRef.current = null;
      }
      if (container) container.innerHTML = '';
    };
  }, [analysisViz?.generatedAt, analysisViz?.indicators?.lastClose, bars, datafeedApiBasePath, entryZone, invalidation, ready, setupColor, symbol, target]);

  if (!ready) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {loading ? <CircularProgress sx={{ color: COLORS.gold }} /> : <Typography sx={{ color: COLORS.muted }}>No analysis bars loaded.</Typography>}
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%', borderRadius: 1.5, overflow: 'hidden', bgcolor: COLORS.surfaceDark }}>
      <Box ref={containerRef} sx={{ width: '100%', height: '100%' }} />
      {tvError && (
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(16,20,31,0.88)' }}>
          <Typography sx={{ color: COLORS.orange, fontWeight: 800 }}>{tvError}</Typography>
        </Box>
      )}
    </Box>
  );
}

function DecisionReadout({ label, value, sub, color = COLORS.platinum, accent = color }: { label: string; value: string; sub?: string; color?: string; accent?: string }) {
  return (
    <Box sx={{
      p: 1.7,
      borderRadius: 2,
      bgcolor: COLORS.surfaceDark,
      border: `1px solid ${COLORS.border}`,
      borderLeft: `3px solid ${accent}`,
      minHeight: 98,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
    }}>
      <Typography variant="caption" sx={{ color: COLORS.muted, textTransform: 'uppercase', fontSize: '0.62rem', letterSpacing: 0.8, fontWeight: 800 }}>{label}</Typography>
      <Typography variant="h6" sx={{ color, fontWeight: 900, fontSize: { xs: '1.05rem', md: '1.28rem' }, lineHeight: 1.15, overflowWrap: 'anywhere' }}>{value}</Typography>
      {sub && <Typography variant="caption" sx={{ color: COLORS.muted, fontSize: '0.7rem', lineHeight: 1.35 }}>{sub}</Typography>}
    </Box>
  );
}

function SignalMeter({ label, value, detail, color }: { label: string; value?: number; detail?: string; color?: string }) {
  const normalized = value == null || !Number.isFinite(value) ? 0 : Math.max(0, Math.min(100, value));
  const meterColor = color || scoreColor(normalized);
  return (
    <Box sx={{ mb: 1.1 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, mb: 0.55 }}>
        <Typography variant="caption" sx={{ color: COLORS.platinum, fontWeight: 800, fontSize: '0.72rem' }}>{label}</Typography>
        <Typography variant="caption" sx={{ color: meterColor, fontWeight: 900, fontSize: '0.78rem' }}>{value == null ? '—' : pctLabel(normalized)}</Typography>
      </Box>
      <LinearProgress variant="determinate" value={normalized}
        sx={{ height: 9, borderRadius: 5, bgcolor: COLORS.track, '& .MuiLinearProgress-bar': { bgcolor: meterColor, borderRadius: 5 } }} />
      {detail && <Typography variant="caption" sx={{ color: COLORS.muted, display: 'block', mt: 0.35, fontSize: '0.66rem', lineHeight: 1.3 }}>{detail}</Typography>}
    </Box>
  );
}

function GradeRail({ label, chartGrade, apgeGrade }: { label: string; chartGrade?: ChartGrade | string; apgeGrade?: ChartGrade | string }) {
  const chartScore = gradePoints(chartGrade) * 25;
  const apgeScore = gradePoints(apgeGrade) * 25;
  return (
    <Box sx={{ py: 1.05, borderBottom: `1px solid ${COLORS.border}`, '&:last-of-type': { borderBottom: 0 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.75 }}>
        <Typography variant="caption" sx={{ color: COLORS.platinum, fontWeight: 800, minWidth: 78 }}>{label}</Typography>
        <Box sx={{ display: 'flex', gap: 0.75 }}>
          <Chip label={`Chart ${gradeLabel(chartGrade)}`} size="small" sx={{ height: 21, bgcolor: `${gradeColor(chartGrade)}1F`, color: gradeColor(chartGrade), fontWeight: 900, fontSize: '0.62rem' }} />
          <Chip label={`APGE ${gradeLabel(apgeGrade)}`} size="small" sx={{ height: 21, bgcolor: `${gradeColor(apgeGrade)}1F`, color: gradeColor(apgeGrade), fontWeight: 900, fontSize: '0.62rem' }} />
        </Box>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
        <LinearProgress variant="determinate" value={chartScore} sx={{ height: 7, borderRadius: 4, bgcolor: COLORS.track, '& .MuiLinearProgress-bar': { bgcolor: gradeColor(chartGrade), borderRadius: 4 } }} />
        <LinearProgress variant="determinate" value={apgeScore} sx={{ height: 7, borderRadius: 4, bgcolor: COLORS.track, '& .MuiLinearProgress-bar': { bgcolor: gradeColor(apgeGrade), borderRadius: 4 } }} />
      </Box>
    </Box>
  );
}

function StatCard({ label, value, sub, color }: { label: string; value: string; sub?: string; color?: string }) {
  return (
    <Box sx={{ p: 2, borderRadius: 2, bgcolor: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
      <Typography variant="caption" sx={{ color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.65rem' }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 800, color: color || COLORS.platinum, mt: 0.25 }}>
        {value}
      </Typography>
      {sub && <Typography variant="caption" sx={{ color: COLORS.muted }}>{sub}</Typography>}
    </Box>
  );
}

function EquityCurve({ trades }: { trades: TopStepRiskState['tradeLog'] }) {
  const closed = [...trades]
    .filter(t => t.status === 'closed' && t.realizedPnl != null && t.closedAt)
    .sort((a, b) => new Date(a.closedAt!).getTime() - new Date(b.closedAt!).getTime());

  if (closed.length < 2) {
    return (
      <Box sx={{ height: 72, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="caption" sx={{ color: COLORS.muted }}>Not enough closed trades to plot curve</Typography>
      </Box>
    );
  }

  const points: number[] = [0];
  closed.forEach(t => points.push(points[points.length - 1] + (t.realizedPnl ?? 0)));

  const W = 600, H = 72, PAD = 4;
  const minY = Math.min(...points);
  const maxY = Math.max(...points);
  const range = maxY - minY || 1;

  const toX = (i: number) => PAD + (i / (points.length - 1)) * (W - PAD * 2);
  const toY = (v: number) => H - PAD - ((v - minY) / range) * (H - PAD * 2);

  const linePath = points.map((v, i) => `${i === 0 ? 'M' : 'L'}${toX(i).toFixed(1)},${toY(v).toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L${toX(points.length - 1).toFixed(1)},${H} L${toX(0).toFixed(1)},${H} Z`;

  const finalVal = points[points.length - 1];
  const isPositive = finalVal >= 0;
  const lineColor = isPositive ? '#66BB6A' : '#EF5350';
  const fillColor = isPositive ? 'rgba(102,187,106,0.12)' : 'rgba(239,83,80,0.10)';

  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none">
        <path d={areaPath} fill={fillColor} />
        <path d={linePath} fill="none" stroke={lineColor} strokeWidth="1.5" strokeLinejoin="round" />
        {minY < 0 && maxY > 0 && (
          <line x1={PAD} y1={toY(0).toFixed(1)} x2={W - PAD} y2={toY(0).toFixed(1)}
            stroke="rgba(255,255,255,0.18)" strokeWidth="1" strokeDasharray="4 3" />
        )}
      </svg>
    </Box>
  );
}

// ─── Page ───────────────────────────────────────────────

export default function TopStepAccountPage({ accountLabel, chipLabel, chipColor, accountMatcher, challengeParams, accountApi, datafeedApiBasePath = '/topstep' }: AccountPageProps) {
  const topstepApi: TopStepAccountApi = accountApi ?? topstepService;
  const [tab, setTab] = useState(0);

  // Account discovery
  const [accountId, setAccountId] = useState<number | null>(null);
  const [accountName, setAccountName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Risk / challenge
  const [risk, setRisk] = useState<TopStepRiskState | null>(null);
  const [progress, setProgress] = useState<ChallengeProgress | null>(null);
  const [_clearConfirm, setClearConfirm] = useState(false);

  // Autopilot
  const [apStatus, setApStatus] = useState<AutopilotStatus | null>(null);
  const [apInstruments, setApInstruments] = useState<string[]>(DEFAULT_AP_INSTRUMENTS);
  const [apInstrumentOptions, setApInstrumentOptions] = useState<TopStepInstrumentMeta[]>(FALLBACK_INSTRUMENT_OPTIONS);
  const decisionLabKazeLoadRef = useRef(false);
  const [apLoading, setApLoading] = useState(false);

  // Kaze signals
  const [kazeSignals, setKazeSignals] = useState<KazeFuturesSignal[]>([]);
  const [kazeMeta, setKazeMeta] = useState<KazeSignalsMeta | null>(null);
  const [kazeLoading, setKazeLoading] = useState(false);
  const [kazeError, setKazeError] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  // Decision visualization
  const [analysisSymbol, setAnalysisSymbol] = useState<string>('MNQ');
  const [analysisSide, setAnalysisSide] = useState<AnalysisSide>('neutral');
  const [analysisViz, setAnalysisViz] = useState<TopStepAnalysisVisualization | null>(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);

  // Chart
  const chartRef = useRef<HTMLDivElement | null>(null);
  const tvRef = useRef<any>(null);
  const [chartContract, setChartContract] = useState<string>('');
  const [contracts, setContracts] = useState<TopStepContract[]>([]);

  // Orders
  const [orders, setOrders] = useState<TopStepOrder[]>([]);
  const [positions, setPositions] = useState<TopStepPosition[]>([]);
  const [orderForm, setOrderForm] = useState({ symbol: 'MNQ', side: 'BUY', contracts: 1, type: 'market', limitPrice: '' });
  const [placingOrder, setPlacingOrder] = useState(false);
  const [orderError, setOrderError] = useState<string | null>(null);

  // Fibonacci Ladder HWM tracker
  const posHwmRef = useRef<Record<string, number>>({});

  const fibLadderPositions: FibLadderPosition[] = React.useMemo(() => {
    return positions.map(p => {
      const sym = (p.contractName || '').replace(/[FGHJKMNQUVXZ]\d{1,2}$/i, '').toUpperCase();
      const pnl = p.unrealizedPnl ?? 0;
      const prevHwm = posHwmRef.current[p.contractId] ?? pnl;
      const hwm = Math.max(prevHwm, pnl);
      posHwmRef.current[p.contractId] = hwm;
      const effectiveStop = hwm >= 400 ? hwm - 100
                          : hwm >= 300 ? hwm - 120
                          : hwm >= 200 ? 100
                          : hwm >= 150 ? 25
                          : -200;
      return { symbol: sym, size: p.size, pnl, hwm, effectiveStop, entryPrice: p.avgEntryPrice, side: p.side === 0 ? 'long' as const : 'short' as const };
    });
  }, [positions]);

  const isRunning = apStatus?.running ?? false;
  const apInstrumentGroups = React.useMemo(() => {
    const grouped = apInstrumentOptions.reduce<Record<string, TopStepInstrumentMeta[]>>((acc, option) => {
      const group = instrumentGroup(option);
      acc[group] = acc[group] || [];
      acc[group].push(option);
      return acc;
    }, {});

    return INSTRUMENT_GROUP_ORDER
      .filter(group => (grouped[group]?.length || 0) > 0)
      .map(group => ({ label: group, items: grouped[group].sort((a, b) => a.symbol.localeCompare(b.symbol)) }));
  }, [apInstrumentOptions]);

  // ── Account Discovery ─────────────────────────────────

  const discoverAccount = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const accounts = await topstepApi.getAccounts();
      const match = accountMatcher(accounts);
      if (match) {
        setAccountId(match.accountId);
        setAccountName(match.accountName);
        setError(null);
      } else {
        const found = accounts.map(a => `${a.accountId}:${a.accountName}`).join(', ');
        setError(`Account not found. Discovered ${accounts.length} TopStep accounts: [${found}]. Retry after the backend refreshes ProjectX account status.`);
      }
    } catch (err: any) {
      const status = err?.response?.status;
      setError(status ? `Failed to discover accounts (HTTP ${status}).` : 'Failed to discover accounts. Check admin API connectivity.');
    } finally {
      setLoading(false);
    }
  }, [accountMatcher, topstepApi]);

  // ── Data Loading ──────────────────────────────────────

  const loadRisk = useCallback(async () => {
    if (!accountId) return;
    try {
      const [r, p] = await Promise.allSettled([
        topstepApi.getAccountRisk(accountId),
        topstepApi.getAccountChallengeProgress(accountId),
      ]);
      if (r.status === 'fulfilled' && r.value) setRisk(r.value);
      if (p.status === 'fulfilled' && p.value) setProgress(p.value);
    } catch { /* non-fatal */ }
  }, [accountId, topstepApi]);

  const loadAutopilotStatus = useCallback(async () => {
    if (!accountId) return;
    try {
      const status = await topstepApi.getAccountAutopilotStatus(accountId);
      if (status) setApStatus(status);
    } catch { /* non-fatal */ }
  }, [accountId, topstepApi]);

  const loadOrders = useCallback(async () => {
    if (!accountId) return;
    try {
      const [o, p] = await Promise.allSettled([
        topstepApi.getAccountOrders(accountId),
        topstepApi.getAccountPositions(accountId),
      ]);
      if (o.status === 'fulfilled') setOrders(o.value);
      if (p.status === 'fulfilled') setPositions(p.value.positions);
    } catch { /* non-fatal */ }
  }, [accountId, topstepApi]);

  const loadContracts = useCallback(async () => {
    try {
      const { contracts: c, registry } = await topstepApi.getContracts();
      setContracts(c);
      c.forEach(ct => registerTopStepChartContract(ct));
      registry?.forEach(entry => {
        registerContract(entry.symbol, {
          contractId: entry.id,
          name: entry.name,
          description: entry.name,
          tickSize: entry.tickSize,
          tickValue: entry.tickValue,
        });
      });
      if (!chartContract && c.length > 0) {
        setChartContract(normalizeTopStepSymbolKey(c[0].name || c[0].symbolId));
      }
    } catch { /* non-fatal */ }
  }, [chartContract, topstepApi]);

  const loadInstrumentOptions = useCallback(async () => {
    try {
      const registry = await topstepApi.getInstruments?.();
      const options = registry?.instruments || [];
      if (options.length === 0) return;

      const available = new Set(options.map(option => option.symbol));
      const defaults = (registry?.defaults?.length ? registry.defaults : options.filter(option => option.isDefault).map(option => option.symbol))
        .filter(symbol => available.has(symbol));

      setApInstrumentOptions(options);
      setApInstruments(prev => {
        const stillValid = prev.filter(symbol => available.has(symbol));
        const isInitialDefault = prev.length === DEFAULT_AP_INSTRUMENTS.length
          && DEFAULT_AP_INSTRUMENTS.every(symbol => prev.includes(symbol));
        if (stillValid.length === 0 || isInitialDefault) return defaults.length > 0 ? defaults : stillValid;
        return stillValid;
      });
    } catch { /* non-fatal */ }
  }, [topstepApi]);

  const loadKazeSignals = useCallback(async () => {
    setKazeLoading(true);
    setKazeError(null);
    try {
      const [sigs, meta] = await Promise.allSettled([
        topstepApi.getKazeFuturesSignals(),
        topstepApi.getKazeSignalsMeta(),
      ]);
      if (sigs.status === 'fulfilled') {
        setKazeSignals(Array.isArray(sigs.value) ? sigs.value : []);
      } else {
        setKazeError('Kaze signal request failed');
      }
      if (meta.status === 'fulfilled') setKazeMeta(meta.value);
    } catch (err) {
      setKazeError((err as Error).message || 'Kaze signal request failed');
    }
    setKazeLoading(false);
  }, [topstepApi]);

  const loadAnalysisVisualization = useCallback(async () => {
    if (!accountId || !analysisSymbol || !topstepApi.getAccountAnalysisVisualization) return;
    setAnalysisLoading(true);
    setAnalysisError(null);
    try {
      const data = await topstepApi.getAccountAnalysisVisualization(accountId, analysisSymbol, analysisSide);
      setAnalysisViz(data);
      if (data?.contract) registerTopStepChartContract(data.contract, data.symbol);
      if (!data) setAnalysisError('analysis unavailable');
      else if (data.dataStatus !== 'ready') setAnalysisError(data.dataReason || data.dataStatus);
    } catch (err) {
      setAnalysisViz(null);
      setAnalysisError((err as Error).message || 'analysis unavailable');
    } finally {
      setAnalysisLoading(false);
    }
  }, [accountId, analysisSide, analysisSymbol, topstepApi]);

  const loadDecisionLab = useCallback(async () => {
    await Promise.allSettled([
      loadKazeSignals(),
      loadAnalysisVisualization(),
    ]);
  }, [loadAnalysisVisualization, loadKazeSignals]);

  // ── Effects ───────────────────────────────────────────

  useEffect(() => { discoverAccount(); }, [discoverAccount]);

  useEffect(() => {
    if (accountId) {
      loadRisk();
      loadAutopilotStatus();
      loadContracts();
      loadInstrumentOptions();
    }
  }, [accountId, loadRisk, loadAutopilotStatus, loadContracts, loadInstrumentOptions]);

  useEffect(() => {
    if (contracts.length > 0) loadKazeSignals();
  }, [contracts.length, loadKazeSignals]);

  useEffect(() => {
    if (tab === 5 && accountId) loadOrders();
  }, [tab, accountId, loadOrders]);

  useEffect(() => {
    const matchingSignal = kazeSignals.find(signal => signalMatchesSymbol(signal, analysisSymbol));
    if (matchingSignal) setAnalysisSide(sideFromSignal(matchingSignal));
  }, [analysisSymbol, kazeSignals]);

  useEffect(() => {
    if (tab === 3 && accountId) {
      if (kazeSignals.length === 0 && !kazeLoading && !decisionLabKazeLoadRef.current) {
        decisionLabKazeLoadRef.current = true;
        loadKazeSignals();
      }
      loadAnalysisVisualization();
    }
  }, [tab, accountId, kazeSignals.length, kazeLoading, loadAnalysisVisualization, loadKazeSignals]);

  // 30s polling
  useEffect(() => {
    if (!accountId) return;
    const interval = setInterval(async () => {
      await Promise.allSettled([
        loadRisk(),
        loadAutopilotStatus(),
        topstepApi.getAccountPositions(accountId).then(d => setPositions(d.positions)),
      ]);
    }, 30_000);
    return () => clearInterval(interval);
  }, [accountId, loadRisk, loadAutopilotStatus, topstepApi]);

  // Chart initialization
  useEffect(() => {
    const container = chartRef.current;
    if (tab !== 4 || !chartContract || !container) return;
    let cancelled = false;
    container.innerHTML = '';

    if (tvRef.current) { try { tvRef.current.remove(); } catch {} tvRef.current = null; }

    loadTradingViewLibrary().then(() => {
      if (cancelled) return;
      const widget = new (window as any).TradingView.widget({
        container,
        datafeed: createTopStepDatafeed(datafeedApiBasePath),
        library_path: '/charting_library/',
        symbol: `TOPSTEP:${chartContract}`,
        autosize: true,
        interval: '60',
        timezone: 'America/New_York',
        theme: 'Dark',
        locale: 'en',
        toolbar_bg: '#161B27',
        enable_publishing: false,
        hide_side_toolbar: false,
        allow_symbol_change: true,
        loading_screen: { backgroundColor: '#0d1117' },
        disabled_features: ['use_localstorage_for_settings', 'save_chart_properties_to_local_storage', 'header_saveload'],
      });
      tvRef.current = widget;
    }).catch(() => undefined);

    return () => {
      cancelled = true;
      if (tvRef.current) { try { tvRef.current.remove(); } catch {} tvRef.current = null; }
      container.innerHTML = '';
    };
  }, [tab, chartContract, datafeedApiBasePath]);

  // ── Actions ───────────────────────────────────────────

  const handleStartAutopilot = async () => {
    if (!accountId) return;
    setApLoading(true);
    try {
      await topstepApi.startAccountAutopilot(accountId, apInstruments);
      await loadAutopilotStatus();
    } catch { /* logged */ }
    setApLoading(false);
  };

  const handleStopAutopilot = async () => {
    if (!accountId) return;
    setApLoading(true);
    try {
      await topstepApi.stopAccountAutopilot(accountId);
      await loadAutopilotStatus();
    } catch { /* logged */ }
    setApLoading(false);
  };

  const handleClearKillSwitch = async () => {
    if (!accountId) return;
    try {
      await topstepApi.clearAccountKillSwitch(accountId);
      await loadRisk();
      setClearConfirm(false);
    } catch { /* logged */ }
  };

  const handlePlaceOrder = async () => {
    if (!accountId) return;
    setPlacingOrder(true);
    setOrderError(null);
    try {
      await topstepApi.placeAccountOrder(accountId, {
        contractSymbol: orderForm.symbol,
        side: orderForm.side as 'BUY' | 'SELL',
        contracts: orderForm.contracts,
        type: orderForm.type as 'market' | 'limit',
        limitPrice: orderForm.type === 'limit' ? parseFloat(orderForm.limitPrice) : undefined,
      });
      await loadOrders();
    } catch (err: any) {
      setOrderError(err?.response?.data?.error || 'Order failed');
    }
    setPlacingOrder(false);
  };

  const handleCancelOrder = async (orderId: number) => {
    if (!accountId) return;
    if (topstepApi.cancelAccountOrder) {
      await topstepApi.cancelAccountOrder(accountId, orderId);
    } else {
      await topstepApi.cancelOrder(orderId);
    }
    await loadOrders();
  };

  // ── Loading / Error State ─────────────────────────────

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress sx={{ color: COLORS.gold }} />
      </Box>
    );
  }

  if (error || !accountId) {
    return (
      <Box sx={{ py: 6, px: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error || 'Account not provisioned yet.'}
        </Alert>
        <Button onClick={discoverAccount} startIcon={<Refresh />} sx={{ color: COLORS.blue }}>
          Retry Discovery
        </Button>
      </Box>
    );
  }

  // ── Render ────────────────────────────────────────────

  const contractSymbols = contracts.map(c => normalizeFuturesSymbol(c.symbolId || c.name));
  const analysisSymbolOptions = Array.from(new Set([...contractSymbols, ...kazeSignals.map(signal => normalizeFuturesSymbol(signal.symbol || signal.rootKey)), ...DEFAULT_AP_INSTRUMENTS])).sort();
  const analysisRoot = normalizeFuturesSymbol(analysisSymbol);
  const analysisSignal = kazeSignals.find(signal => signalMatchesSymbol(signal, analysisRoot));
  const chartIntel = analysisViz?.chartIntel;
  const apge = chartIntel?.apge;
  const latestDecision = [...(apStatus?.session?.decisions || [])].reverse().find(decision => normalizeFuturesSymbol(decision.symbol) === analysisRoot);
  const recentSymbolDecisions = [...(apStatus?.session?.decisions || [])]
    .filter(decision => normalizeFuturesSymbol(decision.symbol) === analysisRoot)
    .slice(-24);
  const activeSetup = analysisSide === 'short' ? chartIntel?.bestShortSetup : analysisSide === 'long' ? chartIntel?.bestLongSetup : undefined;
  const activeApgeSetup = analysisSide === 'short' ? apge?.bestShort : analysisSide === 'long' ? apge?.bestLong : undefined;
  const activeSetupColor = analysisSide === 'short' ? COLORS.red : COLORS.green;
  const activeEntryZone = activeSetup?.zone || activeApgeSetup?.entryZone;
  const activeTarget = activeSetup?.target || activeApgeSetup?.target;
  const activeInvalidation = activeSetup?.invalidation || activeApgeSetup?.invalidation;
  const gradeRows = [
    { label: 'Structure', chartGrade: chartIntel?.structureGrade, apgeGrade: apge?.structureGrade },
    { label: 'Location', chartGrade: chartIntel?.locationGrade, apgeGrade: apge?.locationGrade },
    { label: 'Timing', chartGrade: chartIntel?.timingGrade, apgeGrade: apge?.timingGrade },
    { label: 'Risk/Reward', chartGrade: chartIntel?.riskRewardGrade, apgeGrade: apge?.riskRewardGrade },
    { label: 'Geometry', chartGrade: chartIntel?.finalChartGrade, apgeGrade: apge?.geometryGrade },
  ];
  const kazeScorePct = analysisSignal ? Math.round(analysisSignal.directionalScore * 100) : undefined;
  const contextScorePct = analysisSignal?.contextScore != null ? Math.round(analysisSignal.contextScore * 100) : undefined;
  const tomiScorePct = analysisSignal ? Math.round(analysisSignal.tomiComposite * 100) : undefined;
  const proxyScorePct = analysisSignal ? Math.round(analysisSignal.avgProxyScore * 100) : undefined;
  const chartScorePct = chartIntel ? gradePoints(chartIntel.finalChartGrade) * 25 : undefined;
  const apgeScorePct = apge ? gradePoints(apge.overallGrade) * 25 : undefined;
  const proxyScoreSeries = (analysisSignal?.proxyScores || []).slice(0, 8).map(proxy => ({
    name: proxy.ticker,
    value: Math.round(proxy.k_score * 100),
    fill: proxy.k_score >= 0.65 ? COLORS.green : proxy.k_score <= 0.35 ? COLORS.red : COLORS.orange,
  }));
  const decisionSeries = recentSymbolDecisions.map((decision, index) => ({
    name: `${index + 1}`,
    time: compactTime(decision.timestamp),
    kaze: Math.round((decision.kazeScore || 0) * 100),
    context: Math.round((decision.contextScore ?? decision.kazeScore ?? 0) * 100),
    tomi: Math.round((decision.tomiComposite || 0) * 100),
    executed: decision.executed ? 100 : 0,
    action: decision.action,
    reason: decision.reason,
  }));
  const contextDirectionDetail = analysisSignal?.contextDirection
    ? `${analysisSignal.contextDirection} score ${contextScorePct ?? 'n/a'} · ${analysisSignal.contextTradeable === false ? 'waiting for tradeable zone' : 'execution context'}`
    : 'Waiting for APGE-aware refresh';

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CurrencyExchange sx={{ color: chipColor, fontSize: 28 }} />
            <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: '-0.04em', color: COLORS.platinum }}>
              {accountLabel}
            </Typography>
            <Chip label={chipLabel} size="small"
              sx={{ bgcolor: `${chipColor}20`, color: chipColor, fontWeight: 700, fontSize: '0.65rem', letterSpacing: 1 }} />
            <Chip label="CONNECTED" size="small" icon={<CheckCircle sx={{ fontSize: '0.8rem !important' }} />}
              sx={{ bgcolor: 'rgba(102,187,106,0.12)', color: COLORS.green, fontWeight: 700, fontSize: '0.65rem' }} />
            {isRunning && (
              <Chip label="AUTOPILOT ACTIVE" size="small" icon={<MonitorHeart sx={{ fontSize: '0.8rem !important' }} />}
                sx={{ bgcolor: 'rgba(79,195,247,0.12)', color: COLORS.blue, fontWeight: 700, fontSize: '0.65rem', letterSpacing: 1 }} />
            )}
          </Box>
          <Typography variant="caption" sx={{ color: COLORS.muted, mt: 0.5, display: 'block' }}>
            {fmt$(challengeParams.accountSize)} Account · Target {fmt$(challengeParams.target)} · Max Loss {fmt$(challengeParams.hardMax)} · {accountName || '—'}
          </Typography>
        </Box>
        <IconButton onClick={() => { loadRisk(); loadAutopilotStatus(); }} size="small" sx={{ color: COLORS.muted, '&:hover': { color: COLORS.platinum } }}>
          <Refresh />
        </IconButton>
      </Box>

      {/* Challenge Progress Bar */}
      {progress && (
        <Box sx={{ mb: 3, p: 2, borderRadius: 2, bgcolor: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: COLORS.platinum }}>Challenge Progress</Typography>
            <Chip label={progress.postureLabel} size="small"
              sx={{ bgcolor: progress.postureLabel === 'HALTED' ? 'rgba(239,83,80,0.15)' : 'rgba(255,255,255,0.08)',
                color: progress.postureLabel === 'HALTED' ? COLORS.red : COLORS.platinum,
                fontWeight: 700, fontSize: '0.65rem' }} />
          </Box>
          <Grid container spacing={2} sx={{ mb: 1.5 }}>
            <Grid item xs={4}>
              <Typography variant="caption" sx={{ color: COLORS.muted, display: 'block', mb: 0.5 }}>Profit Target</Typography>
              <LinearProgress variant="determinate" value={Math.min(progress.targetPct, 100)}
                sx={{ height: 8, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.08)', '& .MuiLinearProgress-bar': { bgcolor: COLORS.green } }} />
              <Typography variant="caption" sx={{ color: COLORS.green }}>{progress.targetPct.toFixed(1)}% of {fmt$(challengeParams.target)}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" sx={{ color: COLORS.muted, display: 'block', mb: 0.5 }}>Risk Used</Typography>
              <LinearProgress variant="determinate" value={Math.min(progress.riskUsedPct, 100)}
                sx={{ height: 8, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.08)',
                  '& .MuiLinearProgress-bar': { bgcolor: progress.riskUsedPct > 80 ? COLORS.red : progress.riskUsedPct > 50 ? COLORS.orange : COLORS.blue } }} />
              <Typography variant="caption" sx={{ color: COLORS.orange }}>{progress.riskUsedPct.toFixed(1)}% of {fmt$(challengeParams.internalStop)} stop</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" sx={{ color: COLORS.muted, display: 'block', mb: 0.5 }}>P&L Summary</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: pnlColor(risk?.cumulativeRealizedPnl || 0) }}>
                {fmt$(risk?.cumulativeRealizedPnl || 0)} realized
              </Typography>
              <Typography variant="caption" sx={{ color: COLORS.muted }}>{fmt$(risk?.dailyRealizedPnl || 0)} today</Typography>
            </Grid>
          </Grid>
          {risk?.killSwitchActive && (
            <Alert severity="error" icon={<Lock />} sx={{ py: 0.5, fontSize: '0.8rem' }}>
              KILL SWITCH ACTIVE — {risk.killSwitchReason}
            </Alert>
          )}
        </Box>
      )}

      {/* Tabs */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)}
        sx={{ mb: 0, borderBottom: `1px solid ${COLORS.border}`,
          '& .MuiTab-root': { color: COLORS.muted, fontWeight: 600, fontSize: '0.8rem', minWidth: 110 },
          '& .Mui-selected': { color: chipColor },
          '& .MuiTabs-indicator': { bgcolor: chipColor } }}>
        <Tab label="Overview" icon={<MonitorHeart sx={{ fontSize: '1rem' }} />} iconPosition="start" />
        <Tab label="Autopilot" icon={<PlayArrow sx={{ fontSize: '1rem' }} />} iconPosition="start" />
        <Tab label="Kaze Signals" icon={<Air sx={{ fontSize: '1rem' }} />} iconPosition="start" />
        <Tab label="Decision Lab" icon={<Insights sx={{ fontSize: '1rem' }} />} iconPosition="start" />
        <Tab label="Chart" icon={<ShowChart sx={{ fontSize: '1rem' }} />} iconPosition="start" />
        <Tab label="Orders" icon={<Send sx={{ fontSize: '1rem' }} />} iconPosition="start" />
      </Tabs>

      {/* ── Tab 0: Overview ── */}
      <TabPanel value={tab} index={0}>
        <Grid container spacing={2.5}>
          {/* KPIs */}
          <Grid item xs={12} sm={6} md={3}>
            <StatCard label="Cumulative P&L" value={fmt$(risk?.cumulativeRealizedPnl || 0, 2)}
              sub={`Today: ${fmt$(risk?.dailyRealizedPnl || 0, 2)}`}
              color={pnlColor(risk?.cumulativeRealizedPnl || 0)} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard label="Distance to Target" value={fmt$(risk?.distanceToTarget ?? challengeParams.target)}
              sub={`${challengeParams.target > 0 ? ((1 - (risk?.distanceToTarget ?? challengeParams.target) / challengeParams.target) * 100).toFixed(1) : '0.0'}% complete`}
              color={COLORS.gold} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard label="Distance to Stop" value={fmt$(risk?.distanceToInternalStop ?? challengeParams.internalStop)}
              sub={`Hard max: ${fmt$(challengeParams.hardMax)}`}
              color={(risk?.distanceToInternalStop ?? challengeParams.internalStop) < 500 ? COLORS.red : COLORS.platinum} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard label="Win Rate"
              value={risk?.totalTrades ? `${Math.round((risk.wins / risk.totalTrades) * 100)}%` : '—'}
              sub={`${risk?.wins ?? 0}W / ${risk?.losses ?? 0}L · ${risk?.totalTrades ?? 0} trades`}
              color={risk?.totalTrades && risk.wins / risk.totalTrades >= 0.5 ? COLORS.green : COLORS.orange} />
          </Grid>

          {/* Risk Engine State */}
          <Grid item xs={12}>
            <Box sx={{ p: 2.5, borderRadius: 2, bgcolor: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: COLORS.platinum }}>
                  Risk Engine State
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {risk?.killSwitchActive ? (
                    <Button size="small" variant="outlined" startIcon={<LockOpen />}
                      onClick={handleClearKillSwitch}
                      sx={{ borderColor: COLORS.green, color: COLORS.green, fontSize: '0.72rem' }}>
                      Clear Kill Switch
                    </Button>
                  ) : null}
                </Box>
              </Box>
              {risk ? (
                <Grid container spacing={2}>
                  {[
                    ['Total Trades', `${risk.totalTrades}`],
                    ['Wins / Losses', `${risk.wins}W / ${risk.losses}L`],
                    ['Win Rate', risk.totalTrades ? `${Math.round((risk.wins / risk.totalTrades) * 100)}%` : '—'],
                    ['Consec. Losses', `${risk.consecutiveLosses}`],
                    ['Daily Cap Reached', risk.dailyCapReached ? 'YES' : 'No'],
                    ['Kill Switch', risk.killSwitchActive ? 'ACTIVE' : 'Off'],
                    ['Posture', risk.posture.toUpperCase()],
                    ['Session Date', risk.sessionDate],
                  ].map(([k, v]) => (
                    <Grid item xs={6} sm={3} key={k}>
                      <Typography variant="caption" sx={{ color: COLORS.muted, display: 'block', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.8 }}>{k}</Typography>
                      <Typography variant="body2" sx={{
                        fontWeight: 700,
                        color: k === 'Kill Switch' && v === 'ACTIVE' ? COLORS.red
                          : k === 'Daily Cap Reached' && v === 'YES' ? COLORS.orange
                          : k === 'Posture' && v === 'HALTED' ? COLORS.red
                          : COLORS.platinum
                      }}>{v}</Typography>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography variant="body2" sx={{ color: COLORS.muted }}>Loading risk state...</Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </TabPanel>

      {/* ── Tab 1: Autopilot ── */}
      <TabPanel value={tab} index={1}>
        <Grid container spacing={2.5}>
          <Grid item xs={12} md={4}>
            <Box sx={{ p: 2.5, borderRadius: 2, bgcolor: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: COLORS.platinum, mb: 2 }}>
                Autonomous Trading
              </Typography>
              <Typography variant="body2" sx={{ color: COLORS.muted, mb: 2, lineHeight: 1.7 }}>
                Tomi evaluates market conditions via Kaze directional scoring. Signals passing the risk engine are submitted as futures orders on ProjectX.
              </Typography>
              {/* Multi-instrument selector */}
              {apInstrumentGroups.map(group => (
                <Box key={group.label} sx={{ mb: 1.5 }}>
                  <Typography variant="caption" sx={{ color: COLORS.muted, fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', mb: 0.5 }}>
                    {group.label}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {group.items.map((instrument) => {
                      const active = apInstruments.includes(instrument.symbol);
                      return (
                        <Tooltip key={instrument.symbol} title={instrument.fullName} placement="top">
                          <Chip label={instrument.symbol} size="small"
                            onClick={() => {
                              if (isRunning) return;
                              setApInstruments(prev => prev.includes(instrument.symbol) ? prev.filter(s => s !== instrument.symbol) : [...prev, instrument.symbol]);
                            }}
                            sx={{
                              cursor: isRunning ? 'default' : 'pointer',
                              fontWeight: 700, fontSize: '0.65rem', height: 22,
                              bgcolor: active ? 'rgba(79,195,247,0.15)' : 'rgba(255,255,255,0.05)',
                              color: active ? COLORS.blue : COLORS.muted,
                              border: active ? `1px solid ${COLORS.blue}40` : `1px solid transparent`,
                              '&:hover': isRunning ? {} : { bgcolor: active ? 'rgba(79,195,247,0.22)' : 'rgba(255,255,255,0.09)' },
                            }} />
                        </Tooltip>
                      );
                    })}
                  </Box>
                </Box>
              ))}
              <Typography variant="caption" sx={{ color: COLORS.muted, fontSize: '0.68rem', display: 'block', mb: 1.5 }}>
                {apInstruments.length} instrument{apInstruments.length !== 1 ? 's' : ''} selected
              </Typography>
              {isRunning ? (
                <Button fullWidth variant="contained" onClick={handleStopAutopilot} disabled={apLoading}
                  startIcon={apLoading ? <CircularProgress size={14} /> : <Stop />}
                  sx={{ bgcolor: COLORS.red, '&:hover': { bgcolor: '#C62828' }, fontWeight: 800 }}>
                  Stop Autopilot
                </Button>
              ) : (
                <Button fullWidth variant="contained" onClick={handleStartAutopilot}
                  disabled={apLoading || apInstruments.length === 0}
                  startIcon={apLoading ? <CircularProgress size={14} /> : <PlayArrow />}
                  sx={{ bgcolor: COLORS.green, color: '#000', '&:hover': { bgcolor: '#43A047' }, fontWeight: 800 }}>
                  Launch Tomi ({apInstruments.length} instruments)
                </Button>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={8}>
            <Box sx={{ p: 2.5, borderRadius: 2, bgcolor: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: COLORS.platinum, mb: 1.5 }}>
                Session Status
              </Typography>
              {apStatus?.session ? (
                <>
                  <Grid container spacing={1.5} sx={{ mb: 2 }}>
                    {[
                      ['Status', apStatus.session.status.toUpperCase(), apStatus.session.status === 'running' ? COLORS.green : COLORS.muted],
                      ['Instruments', (apStatus.session.instrumentList?.join(', ') || apStatus.session.primaryInstrument), COLORS.gold],
                      ['Cycles', `${apStatus.session.cycles}`, COLORS.platinum],
                      ['Trades Executed', `${apStatus.session.stats.tradesExecuted}`, COLORS.platinum],
                      ['Risk Rejected', `${apStatus.session.stats.tradesRejectedByRisk}`, COLORS.orange],
                      ['Errors', `${apStatus.session.stats.errors}`, COLORS.platinum],
                    ].map(([k, v, color]) => (
                      <Grid item xs={4} sm={2} key={k}>
                        <Typography variant="caption" sx={{ color: COLORS.muted, display: 'block', fontSize: '0.65rem', textTransform: 'uppercase' }}>{k}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: color as string }}>{v}</Typography>
                      </Grid>
                    ))}
                  </Grid>
                  <Typography variant="caption" sx={{ color: COLORS.muted, display: 'block', mb: 1 }}>
                    Last scan: {apStatus.session.lastScan ? new Date(apStatus.session.lastScan).toLocaleTimeString() : '—'}
                    {apStatus.session.nextScan ? ` · Next: ${new Date(apStatus.session.nextScan).toLocaleTimeString()}` : ''}
                  </Typography>
                  <Divider sx={{ borderColor: COLORS.border, mb: 1.5 }} />
                  <Typography variant="caption" sx={{ color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.65rem' }}>
                    Recent Decisions
                  </Typography>
                  <Box sx={{ maxHeight: 260, overflowY: 'auto', mt: 1 }}>
                    {apStatus.session.decisions.slice().reverse().map(d => (
                      <Box key={d.id} sx={{ py: 1, borderBottom: `1px solid ${COLORS.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                          <Box sx={{ display: 'flex', gap: 0.75, mb: 0.25 }}>
                            <Chip label={d.type.toUpperCase()} size="small"
                              sx={{ fontSize: '0.6rem', height: 18, bgcolor: 'rgba(255,255,255,0.07)', color: COLORS.muted }} />
                            <Chip label={d.action} size="small"
                              sx={{ fontSize: '0.6rem', height: 18,
                                bgcolor: d.action === 'BUY' ? 'rgba(102,187,106,0.15)' : d.action === 'SELL' ? 'rgba(239,83,80,0.15)' : 'rgba(255,255,255,0.07)',
                                color: d.action === 'BUY' ? COLORS.green : d.action === 'SELL' ? COLORS.red : COLORS.muted }} />
                            {d.executed && <Chip label="EXECUTED" size="small" sx={{ fontSize: '0.6rem', height: 18, bgcolor: 'rgba(79,195,247,0.12)', color: COLORS.blue }} />}
                          </Box>
                          <Typography variant="caption" sx={{ color: COLORS.muted, fontSize: '0.7rem' }}>{d.reason}</Typography>
                        </Box>
                        <Typography variant="caption" sx={{ color: COLORS.muted, fontSize: '0.65rem', whiteSpace: 'nowrap', ml: 1 }}>
                          {new Date(d.timestamp).toLocaleTimeString()}
                        </Typography>
                      </Box>
                    ))}
                    {apStatus.session.decisions.length === 0 && (
                      <Typography variant="body2" sx={{ color: COLORS.muted, py: 1 }}>No decisions yet — waiting for next scan cycle.</Typography>
                    )}
                  </Box>
                </>
              ) : (
                <Typography variant="body2" sx={{ color: COLORS.muted }}>No active session. Launch Tomi to begin.</Typography>
              )}
            </Box>
          </Grid>

          {/* Performance Tracker */}
          <Grid item xs={12}>
            <Box sx={{ p: 2.5, borderRadius: 2, bgcolor: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: COLORS.platinum, mb: 2 }}>
                Portfolio Performance
              </Typography>

              {(apStatus?.risk || risk) ? (() => {
                const r = apStatus?.risk || risk!;
                return (
                  <>
                    <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
                      <Grid item xs={6} sm={3}>
                        <StatCard label="Daily P&L" value={fmt$(r.dailyRealizedPnl)} color={r.dailyRealizedPnl >= 0 ? COLORS.green : COLORS.red} />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <StatCard label="Cumulative P&L" value={fmt$(r.cumulativeRealizedPnl)} color={r.cumulativeRealizedPnl >= 0 ? COLORS.green : COLORS.red} />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <StatCard label="Unrealized" value={fmt$(r.cumulativeUnrealizedPnl)} color={r.cumulativeUnrealizedPnl >= 0 ? COLORS.green : COLORS.red} />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <StatCard label="Win Rate"
                          value={r.totalTrades ? `${Math.round((r.wins / r.totalTrades) * 100)}%` : '—'}
                          sub={`${r.wins}W / ${r.losses}L · ${r.totalTrades} trades`}
                          color={r.totalTrades && r.wins / r.totalTrades >= 0.5 ? COLORS.green : COLORS.orange} />
                      </Grid>
                    </Grid>

                    {/* Progress Bars */}
                    <Grid container spacing={2} sx={{ mb: 2.5 }}>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="caption" sx={{ color: COLORS.muted, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.8, display: 'block', mb: 0.5 }}>
                          Distance to Target
                        </Typography>
                        <LinearProgress variant="determinate"
                          value={challengeParams.target > 0 ? Math.min(100, Math.max(0, (1 - r.distanceToTarget / challengeParams.target) * 100)) : 0}
                          sx={{ height: 8, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.08)',
                            '& .MuiLinearProgress-bar': { bgcolor: COLORS.gold, borderRadius: 4 } }} />
                        <Typography variant="caption" sx={{ color: COLORS.gold, fontWeight: 700, mt: 0.5, display: 'block' }}>
                          {fmt$(r.distanceToTarget)} remaining · {fmt$(challengeParams.target)} target
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="caption" sx={{ color: COLORS.muted, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.8, display: 'block', mb: 0.5 }}>
                          Internal Stop Buffer
                        </Typography>
                        <LinearProgress variant="determinate"
                          value={Math.min(100, Math.max(0, (r.distanceToInternalStop / challengeParams.internalStop) * 100))}
                          sx={{ height: 8, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.08)',
                            '& .MuiLinearProgress-bar': { bgcolor: r.distanceToInternalStop < 500 ? COLORS.red : COLORS.orange, borderRadius: 4 } }} />
                        <Typography variant="caption" sx={{ fontWeight: 700, mt: 0.5, display: 'block',
                          color: r.distanceToInternalStop < 500 ? COLORS.red : COLORS.orange }}>
                          {fmt$(r.distanceToInternalStop)} buffer remaining
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="caption" sx={{ color: COLORS.muted, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.8, display: 'block', mb: 0.5 }}>
                          Hard Max Buffer
                        </Typography>
                        <LinearProgress variant="determinate"
                          value={Math.min(100, Math.max(0, (r.distanceToHardMax / challengeParams.hardMax) * 100))}
                          sx={{ height: 8, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.08)',
                            '& .MuiLinearProgress-bar': { bgcolor: r.distanceToHardMax < 300 ? COLORS.red : COLORS.platinum, borderRadius: 4 } }} />
                        <Typography variant="caption" sx={{ fontWeight: 700, mt: 0.5, display: 'block',
                          color: r.distanceToHardMax < 300 ? COLORS.red : COLORS.muted }}>
                          {fmt$(r.distanceToHardMax)} buffer remaining
                        </Typography>
                      </Grid>
                    </Grid>

                    {/* Equity Curve */}
                    {(r.tradeLog?.filter(t => t.status === 'closed').length ?? 0) >= 2 && (
                      <>
                        <Typography variant="caption" sx={{ color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.65rem', display: 'block', mb: 1 }}>
                          Equity Curve
                        </Typography>
                        <Box sx={{ mb: 2.5, borderRadius: 1.5, overflow: 'hidden', border: `1px solid ${COLORS.border}`, bgcolor: COLORS.surface2, px: 1, pt: 1 }}>
                          <EquityCurve trades={r.tradeLog} />
                        </Box>
                      </>
                    )}

                    {/* Fibonacci Recovery Ladder */}
                    {fibLadderPositions.length > 0 && (
                      <Box sx={{ mb: 2.5, borderRadius: 2, overflow: 'hidden', border: `1px solid ${COLORS.border}`, bgcolor: COLORS.surface2, p: 2 }}>
                        <FibonacciLadderViz positions={fibLadderPositions} takeProfitTarget={600} stopLossFloor={-200} accountLabel={accountLabel} />
                      </Box>
                    )}

                    {/* Trade Log */}
                    <Divider sx={{ borderColor: COLORS.border, mb: 1.5 }} />
                    <Typography variant="caption" sx={{ color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.65rem', display: 'block', mb: 1 }}>
                      Trade Log
                    </Typography>
                    {r.tradeLog?.length > 0 ? (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              {['Symbol', 'Side', 'Qty', 'Entry', 'Exit', 'P&L', 'Opened', 'Closed'].map(h => (
                                <TableCell key={h} sx={{ color: COLORS.muted, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.8, borderColor: COLORS.border, py: 0.75, px: 1 }}>{h}</TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {[...r.tradeLog].reverse().slice(0, 15).map(t => (
                              <TableRow key={t.id} sx={{ '&:last-child td': { border: 0 } }}>
                                <TableCell sx={{ color: COLORS.platinum, fontWeight: 700, fontSize: '0.75rem', borderColor: COLORS.border, py: 0.75, px: 1 }}>{t.symbol}</TableCell>
                                <TableCell sx={{ borderColor: COLORS.border, py: 0.75, px: 1 }}>
                                  <Chip label={t.side} size="small" sx={{ fontSize: '0.6rem', height: 18,
                                    bgcolor: t.side === 'LONG' ? 'rgba(102,187,106,0.15)' : 'rgba(239,83,80,0.15)',
                                    color: t.side === 'LONG' ? COLORS.green : COLORS.red }} />
                                </TableCell>
                                <TableCell sx={{ color: COLORS.muted, fontSize: '0.75rem', borderColor: COLORS.border, py: 0.75, px: 1 }}>{t.contracts}</TableCell>
                                <TableCell sx={{ color: COLORS.muted, fontSize: '0.75rem', borderColor: COLORS.border, py: 0.75, px: 1 }}>{t.entryPrice > 0 ? fmtPrice(t.entryPrice) : '—'}</TableCell>
                                <TableCell sx={{ color: COLORS.muted, fontSize: '0.75rem', borderColor: COLORS.border, py: 0.75, px: 1 }}>{t.exitPrice != null ? fmtPrice(t.exitPrice) : '—'}</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', borderColor: COLORS.border, py: 0.75, px: 1,
                                  color: t.realizedPnl == null ? COLORS.muted : t.realizedPnl >= 0 ? COLORS.green : COLORS.red }}>
                                  {t.realizedPnl != null ? fmt$(t.realizedPnl, 2) : t.status === 'open'
                                    ? <Chip label="OPEN" size="small" sx={{ fontSize: '0.6rem', height: 18, bgcolor: 'rgba(79,195,247,0.15)', color: COLORS.blue }} />
                                    : '—'}
                                </TableCell>
                                <TableCell sx={{ color: COLORS.muted, fontSize: '0.68rem', borderColor: COLORS.border, py: 0.75, px: 1 }}>
                                  {t.timestamp ? new Date(t.timestamp).toLocaleTimeString() : '—'}
                                </TableCell>
                                <TableCell sx={{ color: COLORS.muted, fontSize: '0.68rem', borderColor: COLORS.border, py: 0.75, px: 1 }}>
                                  {t.closedAt ? new Date(t.closedAt).toLocaleTimeString() : '—'}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography variant="body2" sx={{ color: COLORS.muted, py: 1 }}>No trades recorded yet.</Typography>
                    )}
                  </>
                );
              })() : (
                <Typography variant="body2" sx={{ color: COLORS.muted }}>Loading performance data...</Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </TabPanel>

      {/* ── Tab 2: Kaze Signals ── */}
      <TabPanel value={tab} index={2}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: COLORS.platinum }}>
            Kaze Futures Intelligence
          </Typography>
          <Button size="small" startIcon={<Refresh sx={{ fontSize: '0.8rem' }} />}
            onClick={loadKazeSignals} disabled={kazeLoading} sx={{ color: COLORS.muted }}>
            Refresh Signals
          </Button>
        </Box>

        {kazeLoading && <LinearProgress sx={{ mb: 2, bgcolor: 'rgba(255,255,255,0.05)', '& .MuiLinearProgress-bar': { bgcolor: COLORS.gold } }} />}

        {/* Macro Meta Bar */}
        {kazeMeta && (
          <Box sx={{ mb: 2.5, p: 2, borderRadius: 2, bgcolor: COLORS.surface, border: `1px solid ${COLORS.border}`,
            display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
            {[
              { label: 'Macro Regime', value: kazeMeta.macroRegime, color: COLORS.platinum },
              { label: 'Trend', value: kazeMeta.macroTrend, color: COLORS.platinum },
            ].map(m => (
              <Box key={m.label}>
                <Typography variant="caption" sx={{ color: COLORS.muted, fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: 0.8 }}>{m.label}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: m.color, fontSize: '0.82rem' }}>{m.value}</Typography>
              </Box>
            ))}
            <Box>
              <Typography variant="caption" sx={{ color: COLORS.muted, fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: 0.8 }}>VIX</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700,
                color: kazeMeta.vixLevel >= 30 ? COLORS.red : kazeMeta.vixLevel >= 20 ? COLORS.orange : COLORS.green, fontSize: '0.82rem' }}>
                {kazeMeta.vixLevel.toFixed(1)} <span style={{ color: COLORS.muted, fontWeight: 400, fontSize: '0.7rem' }}>({kazeMeta.vixRegime})</span>
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: COLORS.muted, fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: 0.8 }}>Sentiment</Typography>
              <Chip label={kazeMeta.globalSentiment} size="small"
                sx={{ mt: 0.25, height: 20, fontSize: '0.65rem', fontWeight: 800,
                  bgcolor: kazeMeta.globalSentiment === 'BULLISH' ? 'rgba(102,187,106,0.15)' : kazeMeta.globalSentiment === 'BEARISH' ? 'rgba(239,83,80,0.15)' : 'rgba(255,255,255,0.07)',
                  color: kazeMeta.globalSentiment === 'BULLISH' ? COLORS.green : kazeMeta.globalSentiment === 'BEARISH' ? COLORS.red : COLORS.muted }} />
            </Box>
            <Box sx={{ ml: 'auto' }}>
              <Typography variant="caption" sx={{ color: COLORS.muted, fontSize: '0.63rem' }}>
                {kazeSignals.length} contracts · {new Date(kazeMeta.generatedAt).toLocaleTimeString()}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Category filter */}
        {kazeSignals.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2.5, flexWrap: 'wrap' }}>
            {(['all', 'equity_index', 'commodity', 'fx', 'rates'] as const).map(cat => {
              const count = cat === 'all' ? kazeSignals.length : kazeSignals.filter(s => s.category === cat).length;
              const label = cat === 'all' ? 'All' : cat === 'equity_index' ? 'Equity Index' : cat === 'commodity' ? 'Commodity' : cat === 'fx' ? 'FX' : 'Rates';
              return (
                <Chip key={cat} label={`${label} (${count})`} size="small" onClick={() => setCategoryFilter(cat)}
                  sx={{ cursor: 'pointer', fontWeight: 700, fontSize: '0.7rem',
                    bgcolor: categoryFilter === cat ? chipColor : 'rgba(255,255,255,0.07)',
                    color: categoryFilter === cat ? '#000' : COLORS.muted,
                    '&:hover': { bgcolor: categoryFilter === cat ? chipColor : 'rgba(255,255,255,0.12)' } }} />
              );
            })}
          </Box>
        )}

        {/* Signal Cards */}
        <Grid container spacing={2.5}>
          {kazeSignals.filter(s => categoryFilter === 'all' || s.category === categoryFilter).map(sig => (
            <Grid item xs={12} md={6} key={sig.contractId}>
              <Box sx={{ p: 2.5, borderRadius: 2, bgcolor: COLORS.surface, border: `1px solid ${COLORS.border}`,
                borderLeft: `3px solid ${directionColor(sig.direction)}` }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 900, color: COLORS.gold, lineHeight: 1 }}>{sig.symbol}</Typography>
                      <Typography variant="caption" sx={{ color: COLORS.muted, fontSize: '0.68rem' }}>
                        {sig.category === 'equity_index' ? 'Equity Index' : sig.category === 'commodity' ? 'Commodity' : sig.category === 'fx' ? 'FX' : 'Rates'}
                      </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ color: COLORS.muted }}>{sig.contractName}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <Chip label={sig.direction} size="small"
                      sx={{ bgcolor: sig.direction === 'LONG' ? 'rgba(102,187,106,0.15)' : sig.direction === 'SHORT' ? 'rgba(239,83,80,0.15)' : 'rgba(255,255,255,0.07)',
                        color: directionColor(sig.direction), fontWeight: 800, fontSize: '0.65rem' }} />
                    <Chip label={sig.confidence} size="small"
                      sx={{ bgcolor: 'rgba(255,255,255,0.07)', color: sig.confidence === 'HIGH' ? COLORS.green : sig.confidence === 'MEDIUM' ? COLORS.orange : COLORS.muted,
                        fontWeight: 700, fontSize: '0.65rem' }} />
                  </Box>
                </Box>
                <Box sx={{ mb: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                    <Typography variant="caption" sx={{ color: COLORS.muted }}>Directional Score</Typography>
                    <Typography variant="caption" sx={{ color: COLORS.muted }}>Tomi {(sig.tomiComposite * 100).toFixed(0)}%</Typography>
                  </Box>
                  {scoreBar(sig.directionalScore)}
                </Box>
                {sig.proxyScores && sig.proxyScores.length > 0 && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" sx={{ color: COLORS.muted, display: 'block', mb: 0.6, textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: 0.8 }}>
                      Proxy Equity Signals
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {sig.proxyScores.slice(0, 5).map(ps => (
                        <Box key={ps.ticker} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="caption" sx={{ color: COLORS.muted, fontSize: '0.68rem', minWidth: 44, fontWeight: 600 }}>{ps.ticker}</Typography>
                          <LinearProgress variant="determinate" value={Math.round(ps.k_score * 100)}
                            sx={{ flex: 1, height: 5, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.06)',
                              '& .MuiLinearProgress-bar': { bgcolor: ps.k_score >= 0.7 ? COLORS.green : ps.k_score <= 0.3 ? COLORS.red : COLORS.orange, borderRadius: 3 } }} />
                          <Typography variant="caption" sx={{ color: ps.k_score >= 0.7 ? COLORS.green : ps.k_score <= 0.3 ? COLORS.red : COLORS.orange,
                            fontWeight: 700, fontSize: '0.65rem', minWidth: 32, textAlign: 'right' }}>
                            {Math.round(ps.k_score * 100)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
                {(sig.longDrivers?.length > 0 || sig.shortDrivers?.length > 0) && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" sx={{ color: COLORS.muted, display: 'block', mb: 0.6, textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: 0.8 }}>Drivers</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {sig.longDrivers?.slice(0, 3).map((d, i) => (
                        <Chip key={`L${i}`} label={d} size="small" sx={{ fontSize: '0.6rem', height: 18, bgcolor: 'rgba(102,187,106,0.10)', color: COLORS.green, fontWeight: 600 }} />
                      ))}
                      {sig.shortDrivers?.slice(0, 3).map((d, i) => (
                        <Chip key={`S${i}`} label={d} size="small" sx={{ fontSize: '0.6rem', height: 18, bgcolor: 'rgba(239,83,80,0.10)', color: COLORS.red, fontWeight: 600 }} />
                      ))}
                    </Box>
                  </Box>
                )}
                <Box sx={{ mb: sig.briefingExcerpt ? 1.5 : 0 }}>
                  <Typography variant="caption" sx={{ color: COLORS.muted, display: 'block', mb: 0.25 }}>Rationale</Typography>
                  <Typography variant="body2" sx={{ color: COLORS.platinum, fontSize: '0.75rem', lineHeight: 1.6 }}>{sig.reason}</Typography>
                </Box>
                {sig.topNews.length > 0 && (
                  <>
                    <Divider sx={{ borderColor: COLORS.border, mb: 1 }} />
                    <Typography variant="caption" sx={{ color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.6rem', display: 'block', mb: 0.75 }}>
                      Driving News
                    </Typography>
                    {sig.topNews.slice(0, 3).map((n, i) => (
                      <Box key={i} sx={{ display: 'flex', gap: 1, mb: 0.75, alignItems: 'flex-start' }}>
                        <Chip label={n.impact} size="small"
                          sx={{ fontSize: '0.55rem', height: 16,
                            bgcolor: n.impact === 'HIGH' ? 'rgba(239,83,80,0.15)' : n.impact === 'MEDIUM' ? 'rgba(255,167,38,0.15)' : 'rgba(255,255,255,0.07)',
                            color: n.impact === 'HIGH' ? COLORS.red : n.impact === 'MEDIUM' ? COLORS.orange : COLORS.muted }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" sx={{ color: COLORS.platinum, fontSize: '0.72rem', lineHeight: 1.4, display: 'block' }}>{n.headline}</Typography>
                          <Typography variant="caption" sx={{ color: COLORS.muted, fontSize: '0.63rem' }}>
                            {n.source} · {new Date(n.timestamp).toLocaleTimeString()}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </>
                )}
                <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                  <Button size="small" onClick={() => { setAnalysisSymbol(sig.symbol); setAnalysisSide(sideFromSignal(sig)); setTab(3); }}
                    startIcon={<Insights sx={{ fontSize: '0.8rem' }} />}
                    sx={{ color: COLORS.gold, fontSize: '0.7rem', border: `1px solid ${COLORS.gold}30` }}>
                    Lab
                  </Button>
                  <Button size="small" onClick={() => { setChartContract(sig.symbol); setTab(4); }}
                    startIcon={<ShowChart sx={{ fontSize: '0.8rem' }} />}
                    sx={{ color: COLORS.blue, fontSize: '0.7rem', border: `1px solid ${COLORS.blue}30` }}>
                    Chart
                  </Button>
                  {sig.direction !== 'FLAT' && (
                    <Button size="small" onClick={() => {
                      setOrderForm(f => ({ ...f, symbol: sig.symbol, side: sig.direction === 'LONG' ? 'BUY' : 'SELL' }));
                      setTab(5);
                    }}
                    sx={{ color: directionColor(sig.direction), fontSize: '0.7rem',
                      border: `1px solid ${directionColor(sig.direction)}30` }}>
                      {sig.direction === 'LONG' ? 'BUY' : 'SELL'} {sig.symbol}
                    </Button>
                  )}
                </Box>
              </Box>
            </Grid>
          ))}
          {kazeSignals.length === 0 && !kazeLoading && (
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ color: COLORS.muted }}>
                {contracts.length === 0 ? 'Loading contracts...' : 'Generating signals — refresh to check.'}
              </Typography>
            </Grid>
          )}
        </Grid>
      </TabPanel>

      {/* ── Tab 3: Decision Lab ── */}
      <TabPanel value={tab} index={3}>
        <Grid container spacing={2.5}>
          <Grid item xs={12}>
            <Box sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: COLORS.surface,
              border: `1px solid ${COLORS.border}`,
              display: 'flex',
              gap: 1.5,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}>
              <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center', flexWrap: 'wrap', flex: 1 }}>
                <FormControl size="small" sx={{ minWidth: 136 }}>
                  <InputLabel sx={{ color: COLORS.muted }}>Symbol</InputLabel>
                  <Select value={analysisSymbol} label="Symbol" onChange={e => setAnalysisSymbol(e.target.value)}
                    sx={{ color: COLORS.platinum, fontWeight: 800, '.MuiOutlinedInput-notchedOutline': { borderColor: COLORS.border } }}>
                    {analysisSymbolOptions.map(symbol => <MenuItem key={symbol} value={symbol}>{symbol}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 128 }}>
                  <InputLabel sx={{ color: COLORS.muted }}>Side</InputLabel>
                  <Select value={analysisSide} label="Side" onChange={e => setAnalysisSide(e.target.value as AnalysisSide)}
                    sx={{ color: analysisSide === 'long' ? COLORS.green : analysisSide === 'short' ? COLORS.red : COLORS.muted, fontWeight: 800, '.MuiOutlinedInput-notchedOutline': { borderColor: COLORS.border } }}>
                    <MenuItem value="neutral">Neutral</MenuItem>
                    <MenuItem value="long">Long</MenuItem>
                    <MenuItem value="short">Short</MenuItem>
                  </Select>
                </FormControl>
                <Button onClick={loadDecisionLab} disabled={(analysisLoading || kazeLoading) || !topstepApi.getAccountAnalysisVisualization}
                  startIcon={(analysisLoading || kazeLoading) ? <CircularProgress size={14} /> : <Refresh sx={{ fontSize: '0.9rem' }} />}
                  sx={{ color: COLORS.blue, border: `1px solid ${COLORS.blue}35`, height: 40, fontWeight: 800 }}>
                  Refresh
                </Button>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Chip label="READ ONLY" size="small" sx={{ bgcolor: 'rgba(102,187,106,0.12)', color: COLORS.green, fontWeight: 900 }} />
                {analysisViz?.generatedAt && (
                  <Typography variant="caption" sx={{ color: COLORS.muted, fontWeight: 700 }}>Updated {compactDateTime(analysisViz.generatedAt)}</Typography>
                )}
              </Box>
            </Box>
            {analysisError && (
              <Alert severity="warning" sx={{ mt: 1.5, bgcolor: 'rgba(255,167,38,0.08)', color: COLORS.orange, border: `1px solid ${COLORS.orange}30` }}>
                {analysisError}
              </Alert>
            )}
          </Grid>

          <Grid item xs={12} sm={6} lg={3}>
            <DecisionReadout
              label="Market Signal"
              value={analysisSignal ? `${analysisSignal.direction} ${pctLabel(kazeScorePct)}` : 'No Kaze Signal'}
              sub={analysisSignal
                ? `${analysisSignal.confidence} confidence · ${analysisSignal.sentiment.toLowerCase()} sentiment`
                : kazeLoading
                  ? 'Loading Kaze market signal...'
                  : kazeError || 'Kaze data has not mapped this contract yet'}
              color={directionColor(analysisSignal?.direction || 'FLAT')}
              accent={directionColor(analysisSignal?.direction || 'FLAT')}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <DecisionReadout
              label="Chart Gate"
              value={`${gradeLabel(chartIntel?.finalChartGrade)} · ${formatAction(chartIntel?.recommendedAction)}`}
              sub={chartIntel?.setupType ? `${chartIntel.setupType.replace(/_/g, ' ')} · ${chartIntel.auctionPhase.replace(/_/g, ' ')}` : 'Waiting for technical context'}
              color={gradeColor(chartIntel?.finalChartGrade)}
              accent={gradeColor(chartIntel?.finalChartGrade)}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <DecisionReadout
              label="APGE Path"
              value={`${gradeLabel(apge?.overallGrade)} · ${formatAction(apge?.recommendedAction)}`}
              sub={apge ? `${apge.pathBias} ${apge.pathType.replace(/_/g, ' ')} · ${apge.location.replace(/_/g, ' ')}` : 'No auction geometry loaded'}
              color={gradeColor(apge?.overallGrade)}
              accent={gradeColor(apge?.overallGrade)}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <DecisionReadout
              label="Active Price Plan"
              value={analysisSide === 'neutral' ? fmtLevel(analysisViz?.indicators?.lastClose) : fmtLevel(activeEntryZone)}
              sub={analysisSide === 'neutral'
                ? `ATR ${fmtLevel(analysisViz?.indicators?.atr14)} · ${analysisViz?.indicators?.cloudTrend || 'cloud pending'}`
                : `Target ${fmtLevel(activeTarget)} · Invalid ${fmtLevel(activeInvalidation)}`}
              color={analysisSide === 'short' ? COLORS.red : analysisSide === 'long' ? COLORS.green : COLORS.platinum}
              accent={analysisSide === 'short' ? COLORS.red : analysisSide === 'long' ? COLORS.green : COLORS.blue}
            />
          </Grid>

          <Grid item xs={12} lg={8}>
            <Box sx={{ p: 2.2, borderRadius: 2, bgcolor: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, gap: 1, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ color: COLORS.platinum, fontWeight: 900 }}>Price Context</Typography>
                  <Typography variant="caption" sx={{ color: COLORS.muted }}>EMA stack, cloud band, active entry zone, target, and invalidation</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip label={`Close ${fmtLevel(analysisViz?.indicators?.lastClose)}`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.08)', color: COLORS.platinum, fontWeight: 900 }} />
                  <Chip label={`EMA8 ${fmtLevel(analysisViz?.indicators?.ema8)}`} size="small" sx={{ bgcolor: 'rgba(102,187,106,0.10)', color: COLORS.green, fontWeight: 800 }} />
                  <Chip label={`EMA21 ${fmtLevel(analysisViz?.indicators?.ema21)}`} size="small" sx={{ bgcolor: 'rgba(212,175,55,0.12)', color: COLORS.gold, fontWeight: 800 }} />
                  <Chip label={`EMA50 ${fmtLevel(analysisViz?.indicators?.ema50)}`} size="small" sx={{ bgcolor: 'rgba(239,83,80,0.10)', color: COLORS.red, fontWeight: 800 }} />
                </Box>
              </Box>
              <Box sx={{ width: '100%', height: { xs: 430, md: 500 } }}>
                <TradingViewPriceContextChart
                  symbol={analysisRoot}
                  analysisViz={analysisViz}
                  datafeedApiBasePath={datafeedApiBasePath}
                  entryZone={activeEntryZone}
                  target={activeTarget}
                  invalidation={activeInvalidation}
                  setupColor={activeSetupColor}
                  loading={analysisLoading}
                />
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Box sx={{ p: 2.2, borderRadius: 2, bgcolor: COLORS.surface, border: `1px solid ${COLORS.border}`, height: '100%' }}>
              <Typography variant="subtitle2" sx={{ color: COLORS.platinum, fontWeight: 900, mb: 1.25 }}>Decision Stack</Typography>
              <SignalMeter label="Kaze Direction" value={kazeScorePct} color={directionColor(analysisSignal?.direction || 'FLAT')} detail={analysisSignal ? `${analysisSignal.direction} · ${analysisSignal.reason}` : kazeLoading ? 'Loading Kaze market signal...' : kazeError || 'No current signal'} />
              <SignalMeter label="Chart/APGE Direction" value={contextScorePct} color={directionColor(analysisSignal?.contextDirection || 'FLAT')} detail={contextDirectionDetail} />
              <SignalMeter label="Tomi Composite" value={tomiScorePct} color={COLORS.gold} detail={analysisSignal?.macroRegime ? `${analysisSignal.macroRegime} · ${analysisSignal.riskPosture}` : undefined} />
              <SignalMeter label="Chart Gate" value={chartScorePct} color={gradeColor(chartIntel?.finalChartGrade)} detail={chartIntel ? `${gradeLabel(chartIntel.finalChartGrade)} · ${formatAction(chartIntel.recommendedAction)}` : 'No chart verdict'} />
              <SignalMeter label="APGE Gate" value={apgeScorePct} color={gradeColor(apge?.overallGrade)} detail={apge ? `${gradeLabel(apge.overallGrade)} · ${formatAction(apge.recommendedAction)}` : 'No geometry verdict'} />

              <Divider sx={{ my: 1.4, borderColor: COLORS.border }} />
              <Box sx={{ display: 'grid', gap: 0.8 }}>
                {[
                  ['Candidate', analysisSide.toUpperCase(), analysisSide === 'long' ? COLORS.green : analysisSide === 'short' ? COLORS.red : COLORS.muted],
                  ['Latest Decision', latestDecision ? `${formatAction(latestDecision.action)}${latestDecision.executed ? ' · EXEC' : ''}` : 'N/A', latestDecision?.executed ? COLORS.green : COLORS.muted],
                  ['Cloud', analysisViz?.indicators?.cloudTrend || 'N/A', COLORS.blue],
                  ['Slope', analysisViz?.indicators?.emaSlope || 'N/A', analysisViz?.indicators?.emaSlope === 'up' ? COLORS.green : analysisViz?.indicators?.emaSlope === 'down' ? COLORS.red : COLORS.muted],
                ].map(([label, value, color]) => (
                  <Box key={label} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Typography variant="caption" sx={{ color: COLORS.muted, fontWeight: 700 }}>{label}</Typography>
                    <Typography variant="caption" sx={{ color: color as string, fontWeight: 900, textAlign: 'right', maxWidth: '68%', overflowWrap: 'anywhere' }}>{value}</Typography>
                  </Box>
                ))}
              </Box>
              <Divider sx={{ my: 1.6, borderColor: COLORS.border }} />
              <Typography variant="caption" sx={{ color: COLORS.muted, textTransform: 'uppercase', fontWeight: 900 }}>Path Context</Typography>
              <Grid container spacing={1} sx={{ mt: 0.2 }}>
                {[
                  ['Bias', apge?.pathBias],
                  ['Path', apge?.pathType],
                  ['Phase', apge?.auctionPhase],
                  ['Location', apge?.location],
                  ['Setup', chartIntel?.setupType],
                  ['Auction', chartIntel?.auctionPhase],
                ].map(([label, value]) => (
                  <Grid item xs={6} key={label}>
                    <Typography variant="caption" sx={{ color: COLORS.muted, display: 'block', fontSize: '0.62rem' }}>{label}</Typography>
                    <Typography variant="body2" sx={{ color: COLORS.platinum, fontSize: '0.78rem', fontWeight: 900, lineHeight: 1.25, overflowWrap: 'anywhere' }}>{String(value || '—').replace(/_/g, ' ')}</Typography>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2.2, borderRadius: 2, bgcolor: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 1.2 }}>
                <Typography variant="subtitle2" sx={{ color: COLORS.platinum, fontWeight: 900 }}>Technical Grade Rails</Typography>
                <Box sx={{ display: 'flex', gap: 0.75 }}>
                  <Chip label="Chart" size="small" sx={{ height: 21, bgcolor: 'rgba(79,195,247,0.10)', color: COLORS.blue, fontWeight: 900 }} />
                  <Chip label="APGE" size="small" sx={{ height: 21, bgcolor: 'rgba(212,175,55,0.12)', color: COLORS.gold, fontWeight: 900 }} />
                </Box>
              </Box>
              {gradeRows.map(row => <GradeRail key={row.label} label={row.label} chartGrade={row.chartGrade} apgeGrade={row.apgeGrade} />)}
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2.2, borderRadius: 2, bgcolor: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
              <Typography variant="subtitle2" sx={{ color: COLORS.platinum, fontWeight: 900, mb: 1.25 }}>Fundamental and Gate Scores</Typography>
              <SignalMeter label="Kaze Directional Score" value={kazeScorePct} color={directionColor(analysisSignal?.direction || 'FLAT')} detail={analysisSignal?.direction ? `${analysisSignal.direction} · ${analysisSignal.confidence}` : kazeLoading ? 'Loading Kaze market signal...' : kazeError || 'No directional signal'} />
              <SignalMeter label="Tomi Composite" value={tomiScorePct} color={COLORS.gold} detail={analysisSignal?.macroTrend || undefined} />
              <SignalMeter label="Average Proxy Basket" value={proxyScorePct} color={COLORS.blue} detail={analysisSignal?.liquidityState || undefined} />
              <SignalMeter label="Chart Gate Grade" value={chartScorePct} color={gradeColor(chartIntel?.finalChartGrade)} detail={chartIntel ? `${gradeLabel(chartIntel.finalChartGrade)} · ${formatAction(chartIntel.recommendedAction)}` : undefined} />
              <SignalMeter label="APGE Geometry Grade" value={apgeScorePct} color={gradeColor(apge?.overallGrade)} detail={apge ? `${gradeLabel(apge.overallGrade)} · ${formatAction(apge.recommendedAction)}` : undefined} />
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2.2, borderRadius: 2, bgcolor: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
              <Typography variant="subtitle2" sx={{ color: COLORS.platinum, fontWeight: 900, mb: 1.25 }}>Proxy Signal Contributors</Typography>
              <Box sx={{ minHeight: 260 }}>
                {proxyScoreSeries.length > 0 ? (
                  <Box>
                    <Typography variant="caption" sx={{ color: COLORS.muted, display: 'block', mb: 1 }}>
                      {analysisSignal?.proxyQuality || 'DIRECT'} · {analysisSignal?.proxyQualityReason || 'live Kaze proxy basket'}
                    </Typography>
                    {proxyScoreSeries.map(entry => <SignalMeter key={entry.name} label={entry.name} value={entry.value} color={entry.fill} />)}
                  </Box>
                ) : (
                  <Box sx={{ minHeight: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', px: 2 }}>
                    <Typography sx={{ color: COLORS.muted }}>
                      {kazeLoading
                        ? 'Loading proxy basket...'
                        : kazeError
                          ? kazeError
                        : analysisSignal
                          ? `No proxy contributors returned for ${analysisRoot}. ${analysisSignal.proxyQualityReason || ''}`
                          : `No Kaze proxy signal mapped to ${analysisRoot} yet.`}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2.2, borderRadius: 2, bgcolor: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                <Typography variant="subtitle2" sx={{ color: COLORS.platinum, fontWeight: 900 }}>Recent Decision Path</Typography>
                {latestDecision && <Chip label={formatAction(latestDecision.action)} size="small" sx={{ height: 21, bgcolor: 'rgba(255,255,255,0.06)', color: latestDecision.executed ? COLORS.green : COLORS.muted, fontWeight: 900 }} />}
              </Box>
              <Box sx={{ height: 260 }}>
                {decisionSeries.length > 0 ? (
                  <ResponsiveContainer>
                    <AreaChart data={decisionSeries} margin={{ top: 8, right: 12, bottom: 8, left: 0 }}>
                      <defs>
                        <linearGradient id="decisionKaze" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={COLORS.blue} stopOpacity={0.35} /><stop offset="95%" stopColor={COLORS.blue} stopOpacity={0.03} /></linearGradient>
                        <linearGradient id="decisionContext" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={COLORS.green} stopOpacity={0.24} /><stop offset="95%" stopColor={COLORS.green} stopOpacity={0.02} /></linearGradient>
                        <linearGradient id="decisionTomi" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={COLORS.gold} stopOpacity={0.30} /><stop offset="95%" stopColor={COLORS.gold} stopOpacity={0.03} /></linearGradient>
                      </defs>
                      <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                      <XAxis dataKey="time" tick={{ fill: COLORS.muted, fontSize: 11 }} axisLine={{ stroke: COLORS.border }} tickLine={false} minTickGap={18} />
                      <YAxis domain={[0, 100]} tick={{ fill: COLORS.muted, fontSize: 11 }} axisLine={{ stroke: COLORS.border }} tickLine={false} width={36} />
                      <RechartsTooltip contentStyle={{ background: COLORS.surfaceElevated, border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.platinum }} />
                      <Area type="monotone" dataKey="kaze" stroke={COLORS.blue} fill="url(#decisionKaze)" strokeWidth={2} name="Kaze" />
                      <Area type="monotone" dataKey="context" stroke={COLORS.green} fill="url(#decisionContext)" strokeWidth={2} name="Chart/APGE Direction" />
                      <Area type="monotone" dataKey="tomi" stroke={COLORS.gold} fill="url(#decisionTomi)" strokeWidth={2} name="Tomi" />
                      <Line type="stepAfter" dataKey="executed" stroke={COLORS.green} strokeWidth={2} dot={false} name="Executed" />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Typography sx={{ color: COLORS.muted }}>No recent decisions for {analysisSymbol}.</Typography></Box>
                )}
              </Box>
              {latestDecision?.reason && (
                <Typography variant="caption" sx={{ color: COLORS.muted, display: 'block', mt: 1, lineHeight: 1.45 }}>{latestDecision.reason}</Typography>
              )}
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ p: 2.2, borderRadius: 2, bgcolor: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ color: COLORS.platinum, fontWeight: 900 }}>APGE Zones and Conditions</Typography>
                  <Typography variant="caption" sx={{ color: COLORS.muted }}>Entry windows, invalidation, targets, vetoes, and upgrade requirements</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                  <Chip label={`Final ${chartIntel?.finalChartGrade || '—'}`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.06)', color: gradeColor(chartIntel?.finalChartGrade), fontWeight: 800 }} />
                  <Chip label={`Geometry ${apge?.geometryGrade || '—'}`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.06)', color: gradeColor(apge?.geometryGrade), fontWeight: 800 }} />
                  <Chip label={`Action ${apge?.recommendedAction || chartIntel?.recommendedAction || '—'}`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.06)', color: COLORS.platinum, fontWeight: 800 }} />
                </Box>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { color: COLORS.muted, borderColor: COLORS.border, fontWeight: 800 } }}>
                      <TableCell>Side</TableCell>
                      <TableCell>Zone</TableCell>
                      <TableCell>Condition</TableCell>
                      <TableCell>Invalidation</TableCell>
                      <TableCell>Target</TableCell>
                      <TableCell>Grade</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[
                      { side: 'LONG', setup: chartIntel?.bestLongSetup, color: COLORS.green },
                      { side: 'SHORT', setup: chartIntel?.bestShortSetup, color: COLORS.red },
                    ].map(row => (
                      <TableRow key={row.side} sx={{ '& td': { color: COLORS.platinum, borderColor: COLORS.border } }}>
                        <TableCell><Chip label={row.side} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.06)', color: row.color, fontWeight: 800 }} /></TableCell>
                        <TableCell>{fmtLevel(row.setup?.zone)}</TableCell>
                        <TableCell sx={{ maxWidth: 360 }}>{row.setup?.condition || '—'}</TableCell>
                        <TableCell>{fmtLevel(row.setup?.invalidation)}</TableCell>
                        <TableCell>{fmtLevel(row.setup?.target)}</TableCell>
                        <TableCell><Chip label={row.setup?.qualityIfTriggered || '—'} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.06)', color: gradeColor(row.setup?.qualityIfTriggered), fontWeight: 800 }} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {(chartIntel?.vetoReasons?.length || apge?.vetoReasons?.length || chartIntel?.upgradeConditions?.length || apge?.upgradeConditions?.length) ? (
                <Grid container spacing={1.5} sx={{ mt: 1.5 }}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" sx={{ color: COLORS.red, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.8 }}>Vetoes</Typography>
                    {[...(chartIntel?.vetoReasons || []), ...(apge?.vetoReasons || [])].slice(0, 8).map((reason, index) => (
                      <Typography key={index} variant="body2" sx={{ color: COLORS.muted, fontSize: '0.76rem', lineHeight: 1.55 }}>• {reason}</Typography>
                    ))}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" sx={{ color: COLORS.green, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.8 }}>Upgrade Conditions</Typography>
                    {[...(chartIntel?.upgradeConditions || []), ...(apge?.upgradeConditions || [])].slice(0, 8).map((reason, index) => (
                      <Typography key={index} variant="body2" sx={{ color: COLORS.muted, fontSize: '0.76rem', lineHeight: 1.55 }}>• {reason}</Typography>
                    ))}
                  </Grid>
                </Grid>
              ) : null}
            </Box>
          </Grid>
        </Grid>
      </TabPanel>

      {/* ── Tab 4: Chart ── */}
      <TabPanel value={tab} index={4}>
        <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          {contracts.map(c => {
            const sym = (c.symbolId?.replace('F.US.', '') || c.name.replace(/[A-Z]\d+$/, '')).toUpperCase();
            return (
              <Button key={c.id} size="small" variant={chartContract === sym ? 'contained' : 'outlined'}
                onClick={() => setChartContract(sym)}
                sx={{
                  ...(chartContract === sym
                    ? { bgcolor: chipColor, color: '#000', '&:hover': { bgcolor: chipColor } }
                    : { borderColor: COLORS.border, color: COLORS.muted }),
                  fontWeight: 700, fontSize: '0.75rem',
                }}>
                {sym} — {c.name}
              </Button>
            );
          })}
          {contracts.length === 0 && (
            <Typography variant="body2" sx={{ color: COLORS.muted }}>Loading contracts...</Typography>
          )}
        </Box>
        <Box ref={chartRef} sx={{ borderRadius: 2, overflow: 'hidden', border: `1px solid ${COLORS.border}`, minHeight: 540,
          bgcolor: COLORS.surface, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {!chartContract && (
            <Typography variant="body2" sx={{ color: COLORS.muted }}>Select a contract above to load its chart.</Typography>
          )}
        </Box>
      </TabPanel>

      {/* ── Tab 5: Orders ── */}
      <TabPanel value={tab} index={5}>
        <Grid container spacing={2.5}>
          {/* Manual order entry */}
          <Grid item xs={12} md={4}>
            <Box sx={{ p: 2.5, borderRadius: 2, bgcolor: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: COLORS.platinum, mb: 2 }}>
                Manual Order
              </Typography>
              <Typography variant="caption" sx={{ color: COLORS.orange, display: 'block', mb: 1.5, fontSize: '0.72rem' }}>
                All orders pass through the risk engine veto.
              </Typography>

              <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                <InputLabel sx={{ color: COLORS.muted }}>Symbol</InputLabel>
                <Select value={orderForm.symbol} label="Symbol" onChange={e => setOrderForm(f => ({ ...f, symbol: e.target.value }))}
                  sx={{ color: COLORS.platinum, '.MuiOutlinedInput-notchedOutline': { borderColor: COLORS.border } }}>
                  {contracts.map(c => {
                    const sym = (c.symbolId?.replace('F.US.', '') || c.name.replace(/[A-Z]\d+$/, '')).toUpperCase();
                    return <MenuItem key={c.id} value={sym}>{sym} — {c.description}</MenuItem>;
                  })}
                  {contracts.length === 0 && <MenuItem value="MNQ">MNQ (loading...)</MenuItem>}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                <InputLabel sx={{ color: COLORS.muted }}>Side</InputLabel>
                <Select value={orderForm.side} label="Side" onChange={e => setOrderForm(f => ({ ...f, side: e.target.value }))}
                  sx={{ color: orderForm.side === 'BUY' ? COLORS.green : COLORS.red, '.MuiOutlinedInput-notchedOutline': { borderColor: COLORS.border } }}>
                  <MenuItem value="BUY">BUY (Long)</MenuItem>
                  <MenuItem value="SELL">SELL (Short)</MenuItem>
                </Select>
              </FormControl>

              <TextField fullWidth size="small" label="Contracts" type="number"
                value={orderForm.contracts} onChange={e => setOrderForm(f => ({ ...f, contracts: parseInt(e.target.value) || 1 }))}
                inputProps={{ min: 1, max: 10 }}
                sx={{ mb: 1.5, input: { color: COLORS.platinum }, label: { color: COLORS.muted }, '& .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.border } }} />

              <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                <InputLabel sx={{ color: COLORS.muted }}>Order Type</InputLabel>
                <Select value={orderForm.type} label="Order Type" onChange={e => setOrderForm(f => ({ ...f, type: e.target.value }))}
                  sx={{ color: COLORS.platinum, '.MuiOutlinedInput-notchedOutline': { borderColor: COLORS.border } }}>
                  <MenuItem value="market">Market</MenuItem>
                  <MenuItem value="limit">Limit</MenuItem>
                </Select>
              </FormControl>

              {orderForm.type === 'limit' && (
                <TextField fullWidth size="small" label="Limit Price" type="number"
                  value={orderForm.limitPrice} onChange={e => setOrderForm(f => ({ ...f, limitPrice: e.target.value }))}
                  sx={{ mb: 1.5, input: { color: COLORS.platinum }, label: { color: COLORS.muted }, '& .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.border } }} />
              )}

              {orderError && <Alert severity="error" sx={{ mb: 1.5, py: 0.5, fontSize: '0.78rem' }} onClose={() => setOrderError(null)}>{orderError}</Alert>}

              <Button fullWidth variant="contained" onClick={handlePlaceOrder}
                disabled={placingOrder || risk?.killSwitchActive}
                startIcon={placingOrder ? <CircularProgress size={14} /> : <Send />}
                sx={{ bgcolor: orderForm.side === 'BUY' ? COLORS.green : COLORS.red, color: '#fff',
                  '&:hover': { bgcolor: orderForm.side === 'BUY' ? '#43A047' : '#C62828' }, fontWeight: 800 }}>
                {placingOrder ? 'Placing...' : `${orderForm.side} ${orderForm.contracts} ${orderForm.symbol}`}
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12} md={8}>
            {/* Open positions */}
            <Box sx={{ p: 2.5, borderRadius: 2, bgcolor: COLORS.surface, border: `1px solid ${COLORS.border}`, mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: COLORS.platinum }}>Open Positions</Typography>
                <IconButton size="small" onClick={loadOrders} sx={{ color: COLORS.muted }}><Refresh sx={{ fontSize: '0.9rem' }} /></IconButton>
              </Box>
              {positions.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {['Contract', 'Side', 'Size', 'Avg Entry', 'Last Price', 'Unrealized P&L'].map(h => (
                          <TableCell key={h} sx={{ color: COLORS.muted, fontSize: '0.7rem', borderBottom: `1px solid ${COLORS.border}`, pb: 0.5 }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {positions.map((p, i) => (
                        <TableRow key={i} sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' } }}>
                          <TableCell sx={{ color: COLORS.gold, fontWeight: 700, fontSize: '0.8rem', borderBottom: `1px solid ${COLORS.border}` }}>{p.contractName || p.contractId}</TableCell>
                          <TableCell sx={{ borderBottom: `1px solid ${COLORS.border}` }}>
                            <Chip label={p.side === 0 ? 'LONG' : 'SHORT'} size="small"
                              sx={{ fontSize: '0.65rem', height: 18, bgcolor: p.side === 0 ? 'rgba(102,187,106,0.15)' : 'rgba(239,83,80,0.15)',
                                color: p.side === 0 ? COLORS.green : COLORS.red, fontWeight: 700 }} />
                          </TableCell>
                          <TableCell sx={{ color: COLORS.platinum, fontSize: '0.8rem', borderBottom: `1px solid ${COLORS.border}` }}>{p.size}</TableCell>
                          <TableCell sx={{ color: COLORS.muted, fontSize: '0.8rem', borderBottom: `1px solid ${COLORS.border}` }}>{fmtPrice(p.avgEntryPrice)}</TableCell>
                          <TableCell sx={{ color: COLORS.muted, fontSize: '0.8rem', borderBottom: `1px solid ${COLORS.border}` }}>{fmtPrice(p.currentPrice)}</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', borderBottom: `1px solid ${COLORS.border}`,
                            color: p.pnlIsRealtime === false ? COLORS.muted : (p.unrealizedPnl || 0) >= 0 ? COLORS.green : COLORS.red }}>
                            {p.pnlIsRealtime === false ? '—' : fmt$(p.unrealizedPnl || 0, 2)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" sx={{ color: COLORS.muted }}>No open positions.</Typography>
              )}
            </Box>

            {/* Order History */}
            <Box sx={{ p: 2.5, borderRadius: 2, bgcolor: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: COLORS.platinum, mb: 1.5 }}>Order History</Typography>
              {orders.length > 0 ? (
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {['ID', 'Contract', 'Type', 'Side', 'Size', 'Price', 'Status', 'Time', ''].map(h => (
                          <TableCell key={h} sx={{ bgcolor: COLORS.surface2, color: COLORS.muted, fontSize: '0.7rem', borderBottom: `1px solid ${COLORS.border}`, pb: 0.5 }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[...orders].reverse().map(o => (
                        <TableRow key={o.id} sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' } }}>
                          <TableCell sx={{ color: COLORS.muted, fontSize: '0.75rem', borderBottom: `1px solid ${COLORS.border}` }}>{o.id}</TableCell>
                          <TableCell sx={{ color: COLORS.platinum, fontSize: '0.8rem', borderBottom: `1px solid ${COLORS.border}` }}>{o.contractId}</TableCell>
                          <TableCell sx={{ color: COLORS.muted, fontSize: '0.75rem', borderBottom: `1px solid ${COLORS.border}` }}>{orderTypeLabel(o.type)}</TableCell>
                          <TableCell sx={{ borderBottom: `1px solid ${COLORS.border}` }}>
                            <Chip label={orderSideLabel(o.side)} size="small"
                              sx={{ fontSize: '0.6rem', height: 18, bgcolor: o.side === 0 ? 'rgba(102,187,106,0.15)' : 'rgba(239,83,80,0.15)',
                                color: o.side === 0 ? COLORS.green : COLORS.red, fontWeight: 700 }} />
                          </TableCell>
                          <TableCell sx={{ color: COLORS.platinum, fontSize: '0.8rem', borderBottom: `1px solid ${COLORS.border}` }}>
                            {o.filledSize > 0 ? `${o.filledSize}/${o.size}` : o.size}
                          </TableCell>
                          <TableCell sx={{ color: COLORS.muted, fontSize: '0.8rem', borderBottom: `1px solid ${COLORS.border}` }}>
                            {fmtPrice(o.avgFillPrice || o.limitPrice)}
                          </TableCell>
                          <TableCell sx={{ borderBottom: `1px solid ${COLORS.border}` }}>
                            <Chip label={statusLabel(o.status)} size="small"
                              sx={{ fontSize: '0.6rem', height: 18, bgcolor: `${statusColor(o.status)}20`, color: statusColor(o.status), fontWeight: 700 }} />
                          </TableCell>
                          <TableCell sx={{ color: COLORS.muted, fontSize: '0.7rem', borderBottom: `1px solid ${COLORS.border}` }}>
                            {new Date(o.createdAt).toLocaleTimeString()}
                          </TableCell>
                          <TableCell sx={{ borderBottom: `1px solid ${COLORS.border}` }}>
                            {(o.status === 0 || o.status === 1) && (
                              <IconButton size="small" onClick={() => handleCancelOrder(o.id)} sx={{ color: COLORS.red, p: 0.25 }}>
                                <Cancel sx={{ fontSize: '0.9rem' }} />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" sx={{ color: COLORS.muted }}>No orders yet.</Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </TabPanel>
    </Box>
  );
}
